import { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import MrcvSinCarnetModal from "../modal/MrcvSinCarnetModal";
import MrcvConCarnetModal from "../modal/MrcvConCarnetModal";
import { useTerritorio } from "../../context/TerritorioContext";
import { useBacAuth } from "../../context/BacAuthContext";
import { createVisitaBac, updateVisitaBac, editarPaiVacunacion } from "../api/bacSarampionAPI.js";
import API from "../../axios.jsx";
import { obtenerCatalogosCacheados } from "../api/catalogosCache";
import {
  useOnlineStatus,
  guardarOfflineBacVisitaCrear,
  actualizarBorradorPendiente,
  leerFotosPendientesDeVivienda,
  guardarFotoOffline,
  sincronizarFotosBac,
} from "../../hooks/useSync";
import { comprimirImagen, blobABase64, leerArchivoSinComprimir } from "../utils/imageCompression";

const CODIGOS_ERROR_DE_DATOS = [400, 404, 409, 422];
const TOTAL_PASOS = 12;

// ── PAI — Vacunación Sarampión/Rubéola (paso 10) ──────────────────────
// Mismos grupos de edad que usa el formulario de barrido PAI, para que
// los datos queden comparables/articulados entre BAC y PAI.
const RANGOS_EDAD_PAI = [
  { key: "m6_11",    label: "6-11 meses" },
  { key: "m12_17",   label: "12-17 meses" },
  { key: "a18m_10a", label: "18m-10a" },
  { key: "a11_16",   label: "11-16 años" },
  { key: "a17_59",   label: "17-59 años" },
  { key: "a60mas",   label: "60+ años" },
];
const GRUPOS_PAI = [
  { key: "encontrada",          titulo: "Población encontrada según grupos de edad" },
  { key: "vacunada_previa",     titulo: "Población vacunada previamente con Sarampión/Rubéola" },
  { key: "no_vacunada_barrido", titulo: "Población encontrada NO vacunada en el barrido" },
  { key: "vacunada_barrido",    titulo: "Población vacunada durante el barrido" },
];
const MOTIVOS_NO_VACUNADA = [
  { key: "contraindicacion",  label: "Contraindicación médica" },
  { key: "renuente",          label: "Renuente" },
  { key: "factores_salud",    label: "Factores R/C servicio de salud" },
  { key: "factores_familia",  label: "Factores R/C la familia" },
  { key: "otros",              label: "Otros" },
];
const paiGrupoVacio = () => Object.fromEntries(RANGOS_EDAD_PAI.map(r => [r.key, "0"]));
const paiVacio = () => ({
  encontrada: paiGrupoVacio(), vacunada_previa: paiGrupoVacio(),
  no_vacunada_barrido: paiGrupoVacio(), vacunada_barrido: paiGrupoVacio(),
  motivos_no_vacunada: Object.fromEntries(MOTIVOS_NO_VACUNADA.map(m => [m.key, "0"])),
  biologico_pendiente: "No",
  biologico_pendiente_detalle: "",
  carnets: [],
  nombre_vacunador: "",
  nombre_biologico: "",
  lote: "",
});

const INIT = {
  lider_bac: "",
  entrevistador: "",
  tipo_visita: "Primera visita",
  direccion: "",
  lat: null,
  lng: null,
  precision_gps: null,
  tipo_fisico_vivienda: "",
  estado_vivienda: "Efectiva",
  motivo_no_efectiva: "",
  fecha_revisita_sugerida: "",
  nota_revisita: "",
  foto_url: "",
  foto_nombre: "",
  nombre_entrevistado: "",
  tipo_documento: "",
  documento_identificacion: "",
  genero: "",
  telefono: "",
  eps: "",
  otra_eps: "",
  regimen_afiliacion: "",
  grupo_poblacional: "",
  pertenencia_etnica: "",
  numero_personas_entrevistadas: "",
  numero_personas_vivienda: "",
  integrantes: [],
  eisp_encontrado: "",
  eisp_cual: "",
  sintomas_observados: "",
  contactos: [],
  observaciones: "",
  // Comunicación (solo vivienda Efectiva)
  medios_informacion: [],
  sabe_vacunacion: "",
  conoce_personajes_vacunacion: "",
  pai_vacunacion: paiVacio(),
  mrcv: [], // uno por cada persona ≤5 años con evidencia — se llena solo
};

// Lista fija, NO viene de la API — es la que ya usa el resto del sistema
// para "Enfoque diferencial".
// ¿Cómo se entera de la información y actividades de la Secretaría de Salud?
// (se puede marcar más de una)
const MEDIOS_INFORMACION = [
  "Un amigo me cuenta",
  "Por las visitas casa a casa",
  "Por Facebook",
  "Por estados de WhatsApp",
  "Por la emisora",
  "Por perifoneo en mi barrio",
];

const ENFOQUE_DIFERENCIAL = [
  "Ninguno", "Indígena", "Afrodescendiente", "Rom Gitano", "Raizal",
  "LGBTIQ+", "Víctima conflicto", "Discapacidad",
];

const selectStyles = {
  control: (base, state) => ({
    ...base, minHeight: 38, fontSize: 16,
    borderColor: state.selectProps.esError ? "#b3261e" : base.borderColor,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

function siglas(texto) {
  if (!texto) return "—";
  const sinAcentos = texto.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const limpio = sinAcentos.replace(/[^A-Za-z ]/g, "").trim();
  const base = (limpio.split(/\s+/)[0] || "");
  return base.substring(0, 3).toUpperCase() || "—";
}
const pad = n => String(n).padStart(2, "0");
function generarCodigoVivienda(territorio, comunidad) {
  const now = new Date();
  const fecha = pad(now.getDate()) + pad(now.getMonth() + 1) + now.getFullYear();
  const hora  = pad(now.getHours()) + pad(now.getMinutes()) + pad(now.getSeconds()) + String(now.getMilliseconds()).padStart(3, "0");
  return `${siglas(territorio)}-${siglas(comunidad)}-${fecha}-${hora}`;
}

// Título corto de cada paso — se usa en el encabezado y en la barra de progreso
const TITULOS_PASO = {
  1: "Identificación de la visita",
  2: "Ubicación",
  3: "Vivienda",
  4: "Fotografía",
  5: "Datos de contacto",
  6: "Composición del hogar",
  7: "EISP",
  8: "Contactos",
  9: "Comunicación y observaciones",
  10: "PAI — Vacunación",
  11: "MRCV",
  12: "Fotografía",
};

/**
 * modoRevisita=false, viviendaBase=null → vivienda nueva, se genera un
 *   codigo_vivienda nuevo.
 * modoRevisita=true,  viviendaBase=<última fila de esa vivienda> → el
 *   código NO se regenera: se reutiliza viviendaBase.codigo_vivienda,
 *   y se envía "codigo_vivienda_previo" para que el backend calcule el
 *   siguiente numero_visita — SIEMPRE crea una fila nueva.
 */
export default function BacSarampionModal({ show, onClose, reload, modoRevisita, viviendaBase, visitaEditar, jornada = null }) {
  // Visita nueva o revisita: queda dentro de la jornada ABIERTA de la
  // comunidad y el líder de la BAC es el de esa jornada (automático).
  // Al editar se conserva lo que ya tenía el registro.
  const enJornada = !visitaEditar;
  const { data: ctx } = useTerritorio();
  const { usuario } = useBacAuth();

  const online        = useOnlineStatus();

  // El service worker revisa esta bandera antes de aplicar una
  // actualización automática — si hay un formulario a mitad de llenar,
  // espera a que se cierre en vez de recargar la página encima.
  useEffect(() => {
    window.__bacModalAbierto = show;
    if (!show) window.dispatchEvent(new CustomEvent("bac-modal-cerrado"));
  }, [show]);

  // ── Borrador automático ──────────────────────────────────────────────
  // Si la app se cae a mitad de un diligenciamiento (el error boundary
  // atrapa algo y remonta todo desde cero), esto es lo único que evita
  // que la persona tenga que empezar de nuevo. Guarda SIN las fotos en
  // base64 (pesan demasiado para localStorage) — si hay que recuperar
  // el borrador, toca volver a tomar las fotos, pero todo lo escrito
  // (nombres, documentos, edades, respuestas) se recupera intacto.
  const CLAVE_BORRADOR = "bac_borrador_visita_v1";

  const sinFotosPesadas = (obj) => {
    if (Array.isArray(obj)) return obj.map(sinFotosPesadas);
    if (obj && typeof obj === "object") {
      const limpio = {};
      for (const [k, v] of Object.entries(obj)) {
        if (typeof v === "string" && v.startsWith("data:image")) continue; // fotos base64
        limpio[k] = sinFotosPesadas(v);
      }
      return limpio;
    }
    return obj;
  };

  const guardarBorrador = (datos) => {
    try {
      localStorage.setItem(CLAVE_BORRADOR, JSON.stringify({ ...sinFotosPesadas(datos), guardadoEn: Date.now() }));
    } catch { /* localStorage lleno o no disponible — no es crítico */ }
  };
  const leerBorrador = () => {
    try {
      const crudo = localStorage.getItem(CLAVE_BORRADOR);
      return crudo ? JSON.parse(crudo) : null;
    } catch { return null; }
  };
  const limpiarBorrador = () => {
    try { localStorage.removeItem(CLAVE_BORRADOR); } catch { /* no crítico */ }
  };

  const [step,          setStep]         = useState(1);
  const [form,          setForm]         = useState(INIT);
  const [codigo,        setCodigo]       = useState("");
  const [guardando,     setGuardando]    = useState(false);
  const [comprimiendoFoto, setComprimiendoFoto] = useState(false);
  const [fotoLocal, setFotoLocal] = useState(null); // { base64, nombre, pesoOriginalKB, pesoFinalKB }
  const [nuevoCarnet, setNuevoCarnet] = useState({ nombre: "", tipo_identificacion: "", numero_identificacion: "", edad: "", unidad_edad: "Años", presenta_evidencia: "Si", fotoA: null, fotoB: null });
  const [editandoCarnetIdx, setEditandoCarnetIdx] = useState(null);
  const CARNET_VACIO = {
    nombre: "", tipo_identificacion: "", numero_identificacion: "", edad: "", unidad_edad: "Años",
    esRenuente: false,
    presenta_evidencia: "", fotoA: null, fotoB: null, fotoPaiweb: null,
    tiene_vacuna_sarampion: "", se_aplico_vacuna: "", motivo_no_vacunada: "",
    nombre_vacunador: "", nombre_biologico: "", lote: "",
  };

  // Al entrar al paso de PAI, si todavía no se agregó a nadie, el nombre
  // de la primera ficha se precarga con quien recibió la visita — ya se
  // preguntó antes, no hace falta escribirlo de nuevo. La edad NO se
  // precarga porque nunca se pregunta antes de este paso — esa sí toca
  // escribirla siempre.
  useEffect(() => {
    if (step === 10 && form.pai_vacunacion.carnets.length === 0 && !nuevoCarnet.nombre && form.nombre_entrevistado) {
      setNuevoCarnet(p => ({
        ...p,
        nombre: form.nombre_entrevistado,
        tipo_identificacion: form.tipo_documento || p.tipo_identificacion,
        numero_identificacion: form.documento_identificacion || p.numero_identificacion,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Mismo criterio en Contactos: si todavía no se ha agregado ninguno,
  // el primero se precarga con los datos de quien recibió la visita —
  // ya se preguntaron antes, no hace falta volver a escribirlos.
  // "¿Se encuentra enferma?" NO se precarga porque esa pregunta es
  // específica del contacto, nunca se hizo antes.
  useEffect(() => {
    if (step === 8 && form.contactos.length === 0 && !nuevoContacto.nombre_apellidos && form.nombre_entrevistado) {
      setNuevoContacto(p => ({
        ...p,
        nombre_apellidos: form.nombre_entrevistado,
        tipo_identificacion: form.tipo_documento || p.tipo_identificacion,
        documento_identidad: form.documento_identificacion || p.documento_identidad,
        direccion: form.direccion || p.direccion,
        eapb: form.eps || p.eapb,
        otro_eapb: form.otra_eps || p.otro_eapb,
        grupo_poblacional: form.grupo_poblacional || p.grupo_poblacional,
        pertenencia_etnica: form.pertenencia_etnica || p.pertenencia_etnica,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  const MOTIVOS_SIMPLE = [
    { value: "contraindicacion", label: "Contraindicación médica" },
    { value: "factores_salud",   label: "Factores R/C servicio de salud" },
    { value: "factores_familia", label: "Factores R/C la familia" },
    { value: "sin_equipo",       label: "No hay equipo vacunador" },
    { value: "no_aplica",        label: "No aplica" },
    { value: "otros",             label: "Otros" },
  ];
  const MRCV_VACUNAS = [
    { clave: "bcg", nombre: "BCG", grupo: "< 6 Meses" },
    { clave: "rotavirus2", nombre: "Rotavirus 2", grupo: "< 12 Meses" },
    { clave: "neumococo2", nombre: "Neumococo 2", grupo: "< 12 Meses" },
    { clave: "polio3", nombre: "Polio 3", grupo: "< 12 Meses" },
    { clave: "pentavalente3", nombre: "Pentavalente 3", grupo: "< 12 Meses" },
    { clave: "neumococo_refuerzo", nombre: "Neumococo refuerzo", grupo: "12-23 Meses" },
    { clave: "triple_viral_1", nombre: "Triple viral 1ª dosis", grupo: "12-23 Meses" },
    { clave: "fiebre_amarilla", nombre: "Fiebre Amarilla", grupo: "12-23 Meses" },
    { clave: "triple_viral_refuerzo18", nombre: "Triple viral refuerzo 18 meses", grupo: "18-23 Meses" },
    { clave: "hepatitis_a", nombre: "Hepatitis A", grupo: "12-23 Meses" },
    { clave: "triple_viral_2a5", nombre: "Triple viral refuerzo 2-5 años", grupo: "24-60 Meses" },
  ];
  const MRCV_VACIO = {
    nombre_madre: "", documento_madre: "", telefono: "", regimen: "", aseguradora: "",
    condicion_desplazamiento: "NO", etnia: "", tiene_carnet: "SI", motivo_no_vacunacion: "",
    fecha_encuesta: new Date().toISOString().split("T")[0], fecha_nacimiento: "",
  };
  const [formMrcv, setFormMrcv] = useState(MRCV_VACIO);
  const [vacunasMrcv, setVacunasMrcv] = useState({});

  const [comprimiendoCarnet, setComprimiendoCarnet] = useState(false);
  const [menorMrcvSinCarnet, setMenorMrcvSinCarnet] = useState(null);
  const [menorMrcvConCarnet, setMenorMrcvConCarnet] = useState(null);
  const [errors,        setErrors]       = useState({});
  const [gpsCapturando, setGpsCapturando] = useState(false);

  const [catalogos, setCatalogos] = useState({});
  const [cargandoCatalogos, setCargandoCatalogos] = useState(true);

  const [nuevoIntegrante, setNuevoIntegrante] = useState({ nombre: "", edad: "", unidad: "Años" });
  const [nuevoContacto, setNuevoContacto] = useState({
    nombre_apellidos: "", tipo_identificacion: "", documento_identidad: "",
    direccion: "", eapb: "", otro_eapb: "", grupo_poblacional: "",
    pertenencia_etnica: "", etnia_indigena: "", persona_enferma: "", contacto_caso_sospechoso: "", observacion: "",
  });

  useEffect(() => {
    // Los catálogos YA están garantizados en el caché local antes de
    // que este modal pueda siquiera abrirse (los descarga/valida la
    // pantalla de inicio de BacSarampionHome). Leerlos de acá es
    // instantáneo — 0 dependencia de la red en este punto, así que ya
    // no puede quedar "pantalla en blanco" esperando un EPS que nunca
    // llega por internet lento.
    setCargandoCatalogos(true);
    obtenerCatalogosCacheados()
      .then(mapa => setCatalogos(mapa))
      .catch(() => {
        Swal.fire({ icon: "warning", title: "No se pudieron leer las listas guardadas", text: "Intenta cerrar y volver a abrir la app.", timer: 3000, showConfirmButton: false });
      })
      .finally(() => setCargandoCatalogos(false));
  }, []);

  const aArrayForm = (valor) => {
    if (Array.isArray(valor)) return valor;
    if (typeof valor === "string") { try { const p = JSON.parse(valor); return Array.isArray(p) ? p : []; } catch { return []; } }
    return [];
  };

  useEffect(() => {
    if (!show) return;

    if (visitaEditar) {
      // ── Modo edición: precargar todo desde el registro existente ──
      const carnetsBase = visitaEditar.pai_vacunacion || paiVacio();
      setForm({
        ...INIT,
        ...visitaEditar,
        integrantes: aArrayForm(visitaEditar.integrantes),
        contactos: aArrayForm(visitaEditar.contactos),
        medios_informacion: aArrayForm(visitaEditar.medios_informacion),
        sabe_vacunacion: visitaEditar.sabe_vacunacion || "",
        conoce_personajes_vacunacion: visitaEditar.conoce_personajes_vacunacion || "",
        pai_vacunacion: carnetsBase,
      });
      setCodigo(visitaEditar.codigo_vivienda);
      setStep(1);
      setErrors({});

      if (visitaEditar._pendiente) {
        // Borrador sin sincronizar: las fotos NO están en foto_url (ese
        // campo lo llena el servidor al subirlas) — viven en la cola
        // offline, hay que ir a buscarlas ahí para que sí se vean.
        setFotoLocal(null);
        (async () => {
          const fotos = await leerFotosPendientesDeVivienda(visitaEditar.codigo_vivienda);
          const fotoVivienda = fotos.find(f => f.campo === "vivienda");
          if (fotoVivienda) setFotoLocal({ base64: fotoVivienda.base64, esOfflineExistente: true });

          const carnetsConFotos = (carnetsBase.carnets || []).map(c => ({
            ...c,
            fotoA_base64: c.fotoA_base64 || fotos.find(f => f.campo === "carnet_a" && f.refId === c.numero_identificacion)?.base64 || null,
            fotoB_base64: c.fotoB_base64 || fotos.find(f => f.campo === "carnet_b" && f.refId === c.numero_identificacion)?.base64 || null,
            fotoPaiweb_base64: c.fotoPaiweb_base64 || fotos.find(f => f.campo === "carnet_paiweb" && f.refId === c.numero_identificacion)?.base64 || null,
          }));
          if (carnetsConFotos.length) {
            setForm(p => ({ ...p, pai_vacunacion: { ...p.pai_vacunacion, carnets: carnetsConFotos } }));
          }
        })();
      } else {
        setFotoLocal(visitaEditar.foto_url ? { base64: `${API.defaults.baseURL}${visitaEditar.foto_url}`, esExistente: true } : null);
      }
      return;
    }

    setForm({
      ...INIT,
      direccion: modoRevisita ? (viviendaBase?.direccion || "") : "",
      tipo_visita: modoRevisita ? "Revisita" : "Primera visita",
      pai_vacunacion: paiVacio(),
      mrcv: [],
      entrevistador: usuario?.nombre || "",
    });
    setCodigo(modoRevisita ? viviendaBase.codigo_vivienda : generarCodigoVivienda(ctx?.territorio, ctx?.comunidad));
    setFotoLocal(null);
    setNuevoCarnet(CARNET_VACIO);
    setNuevoIntegrante({ nombre: "", edad: "", unidad: "Años" });
    setNuevoContacto({
      nombre_apellidos: "", tipo_identificacion: "", documento_identidad: "",
      direccion: "", eapb: "", otro_eapb: "", grupo_poblacional: "",
      pertenencia_etnica: "", etnia_indigena: "", persona_enferma: "", contacto_caso_sospechoso: "", observacion: "",
    });
    setStep(1);
    setErrors({});

    // ¿Hay un borrador de una vez anterior que se haya interrumpido?
    const borrador = leerBorrador();
    if (borrador?.form) {
      const minutos = Math.round((Date.now() - (borrador.guardadoEn || 0)) / 60000);
      Swal.fire({
        icon: "question",
        title: "Hay un formulario sin terminar",
        html: `Parece que se interrumpió un diligenciamiento hace ${minutos < 1 ? "menos de 1" : minutos} minuto(s) — ¿quieres continuarlo?<br><small>Ojo: las fotos que ya tenías tomadas hay que volver a tomarlas.</small>`,
        showCancelButton: true, showDenyButton: true,
        confirmButtonText: "Continuar borrador", denyButtonText: "Empezar de nuevo",
        cancelButtonText: "Decidir después", confirmButtonColor: "#16342a", denyButtonColor: "#b3261e",
      }).then((res) => {
        if (res.isConfirmed) {
          setForm(borrador.form);
          setCodigo(borrador.codigo || "");
          // La foto NUNCA queda en el borrador (a propósito, por peso) —
          // si el borrador ya iba en un paso más adelante que el de la
          // foto, hay que devolverlo al paso donde toque volver a
          // pedirla. Antes esto era fijo en el paso 4, pero ahora la
          // Efectiva pide la foto al final (paso 11) — se calcula según
          // el estado de vivienda que traía el borrador, no un número fijo.
          const eraEfectivaEnBorrador = borrador.form?.estado_vivienda === "Efectiva";
          const pasoFoto = eraEfectivaEnBorrador ? 12 : 4;
          setStep(borrador.step && borrador.step > pasoFoto ? pasoFoto : (borrador.step || 1));
          setFotoLocal(null);
          if (borrador.nuevoCarnet) setNuevoCarnet(borrador.nuevoCarnet);
        } else if (res.isDenied) {
          limpiarBorrador();
        }
        // "Decidir después" deja el borrador tal cual, sin tocar el form actual
      });
    }
  }, [show, modoRevisita, viviendaBase, visitaEditar, ctx?.territorio, ctx?.comunidad]);

  const set = (campo, valor) => setForm(p => ({ ...p, [campo]: valor }));

  // Autoguardado del borrador — con debounce para no escribir en cada
  // tecla. Solo aplica a visitas NUEVAS (no al editar una ya existente,
  // esa siempre carga sus propios datos reales del servidor).
  useEffect(() => {
    if (!show || visitaEditar) return;
    const t = setTimeout(() => {
      guardarBorrador({ form, codigo, step, nuevoCarnet });
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, visitaEditar, form, codigo, step, nuevoCarnet]);
  const opcionesDe = (tipo) => catalogos[tipo] || [];
  const req = (label) => (<>{label} <span style={{ color: "#b3261e" }}>*</span></>);

  // N° personas en la vivienda = cantidad de integrantes registrados
  // (ya no se digita a mano, se calcula solo).
  useEffect(() => {
    setForm(p => ({ ...p, numero_personas_vivienda: String(p.integrantes.length) }));
  }, [form.integrantes.length]);

  // Con Renuente, No habitada o Abandona no tiene sentido programar
  // revisita (nadie va a volver a tocar esa puerta) — solo aplica
  // cuando hay una chance real de encontrar a alguien la próxima vez.
  const requiereRevisitaPanel = form.estado_vivienda === "No efectiva"
    && (form.motivo_no_efectiva === "Cerrada" || form.motivo_no_efectiva === "Menor en casa");
  const esEfectiva = form.estado_vivienda === "Efectiva";
  const geoCapturada = form.lat != null && form.lng != null;

  // ── Georreferenciación ──────────────────────────────────────────────
  const capturarGPS = () => {
    setGpsCapturando(true);
    if (!navigator.geolocation) {
      Swal.fire("Sin soporte GPS", "Este dispositivo/navegador no permite geolocalización.", "warning");
      setGpsCapturando(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        set("lat", pos.coords.latitude);
        set("lng", pos.coords.longitude);
        set("precision_gps", pos.coords.accuracy);
        setGpsCapturando(false);
      },
      () => {
        Swal.fire("No se pudo capturar la ubicación", "Verifica los permisos de GPS del navegador e inténtalo de nuevo.", "error");
        setGpsCapturando(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // ── Foto: comprime → guarda local SIEMPRE → intenta sincronizar aparte ──
  // Nunca bloquea el guardado de la visita ni depende de tener conexión
  // en el momento — la foto tiene su propia cola (ver useSync.js).
  const handleFoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setComprimiendoFoto(true);
    let base64;
    let pesoFinalKB;
    try {
      const comprimida = await comprimirImagen(file, { maxAncho: 1280, calidad: 0.7 });
      base64 = await blobABase64(comprimida);
      pesoFinalKB = Math.round(comprimida.size / 1024);
    } catch (errorCompresion) {
      // Respaldo — pasa en algunos celulares que no pueden comprimir
      // fotos muy grandes. Se usa la foto tal cual, sin comprimir, en
      // vez de bloquear a la persona.
      console.error("⚠️ Falló la compresión, usando la foto sin comprimir:", errorCompresion);
      try {
        base64 = await leerArchivoSinComprimir(file);
        pesoFinalKB = Math.round(file.size / 1024);
      } catch (errorLectura) {
        console.error("❌ No se pudo leer la foto de ninguna forma:", errorLectura);
        setComprimiendoFoto(false);
        Swal.fire({
          icon: "warning", title: "No se pudo cargar esta foto",
          text: "Puedes intentar con otra foto, o continuar sin ella — no hace falta para poder guardar la visita.",
          confirmButtonText: "Entendido",
        });
        return;
      }
    }

    try {
      setFotoLocal({ base64, nombre: file.name, pesoOriginalKB: Math.round(file.size / 1024), pesoFinalKB });
      await guardarFotoOffline(codigo, base64, file.name);

      // Intento en segundo plano — pero solo tiene sentido si la visita
      // YA existe en el servidor (se está editando una que ya se creó
      // antes). Si es una visita nueva todavía en construcción, la
      // vivienda ni siquiera existe todavía — intentarlo ahora daría
      // 404 garantizado; se sincroniza sola al terminar de guardar
      // (ver el llamado después de createVisitaBac).
      if (visitaEditar) sincronizarFotosBac().catch(() => {});
    } catch (errorGuardado) {
      console.error("❌ Error guardando la foto localmente:", errorGuardado);
      Swal.fire("Error", "No se pudo guardar la foto en el dispositivo. Intenta con otra.", "error");
    } finally {
      setComprimiendoFoto(false);
    }
  };

  // ── Foto de carnet (cara A o B) — mismo patrón que la foto de vivienda ──
  // Comprime y guarda en el mini-formulario de carnet en curso — NO se
  // encola offline todavía (eso pasa recién al hacer clic en "+ Agregar
  // carnet", cuando ya se sabe el número de identificación de la
  // persona, que es la clave con la que se va a adjuntar la foto).
  const handleFotoCarnet = async (e, lado) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setComprimiendoCarnet(true);
    let base64;
    let pesoFinalKB;
    try {
      const comprimida = await comprimirImagen(file, { maxAncho: 1280, calidad: 0.7 });
      base64 = await blobABase64(comprimida);
      pesoFinalKB = Math.round(comprimida.size / 1024);
    } catch (errorCompresion) {
      console.error("⚠️ Falló la compresión del carnet, usando la foto sin comprimir:", errorCompresion);
      try {
        base64 = await leerArchivoSinComprimir(file);
        pesoFinalKB = Math.round(file.size / 1024);
      } catch (errorLectura) {
        console.error("❌ No se pudo leer la foto del carnet de ninguna forma:", errorLectura);
        setComprimiendoCarnet(false);
        Swal.fire({
          icon: "warning", title: "No se pudo cargar esta foto",
          text: "Puedes intentar con otra foto, o continuar sin ella — no es obligatoria para poder agregar a la persona.",
          confirmButtonText: "Entendido",
        });
        return;
      }
    }
    const datos = { base64, nombre: file.name, pesoOriginalKB: Math.round(file.size / 1024), pesoFinalKB };
    setNuevoCarnet(p => (
      lado === "a" ? { ...p, fotoA: datos } : lado === "b" ? { ...p, fotoB: datos } : { ...p, fotoPaiweb: datos }
    ));
    setComprimiendoCarnet(false);
  };

  // Agrega el carnet a la lista y AHÍ SÍ encola las 2 fotos offline,
  // ligadas por numero_identificacion (única forma de saber a cuál
  // carnet pertenece cada una cuando hay varios en la misma vivienda).
  const esMenorDe5 = (c) => {
    const n = parseFloat(c.edad);
    if (isNaN(n)) return false;
    return c.unidad_edad === "Meses" ? n <= 60 : n <= 5;
  };

  // Las fotos del carné solo se piden a 6 años o menos — a partir de 7
  // ya no hace falta evidencia fotográfica (retroalimentación de campo).
  const requiereFotoCarnet = (c) => {
    const n = parseFloat(c.edad);
    if (isNaN(n)) return true; // sin edad clara, se pide igual por seguridad
    return c.unidad_edad === "Meses" ? true : n <= 6;
  };

  // Mismo criterio que calcularMatrizDesdeCarnets del backend — es solo
  // para que el entrevistador vea el avance en vivo, la matriz real la
  // calcula el servidor al guardar (nunca se confía en este resumen).
  const resumenPai = (() => {
    const r = { encontrada: 0, vacunadaPrevia: 0, vacunadaBarrido: 0, noVacunada: 0, renuente: 0 };
    form.pai_vacunacion.carnets.forEach(c => {
      r.encontrada++;
      if (c.presenta_evidencia !== "Si") { r.renuente++; return; }
      if (c.tiene_vacuna_sarampion === "Si") { r.vacunadaPrevia++; return; }
      if (c.se_aplico_vacuna === "Si") r.vacunadaBarrido++;
      else if (c.tiene_vacuna_sarampion === "No") r.noVacunada++;
    });
    return r;
  })();

  const agregarCarnet = async () => {
    const c = nuevoCarnet;

    // ── Renuente: no dio sus datos — solo el nombre es obligatorio.
    //    Ni tipo/N° de documento, ni edad, ni fotos, ni nada de
    //    vacunación se le pide. Se guarda con datos mínimos.
    if (c.esRenuente) {
      if (!c.nombre) {
        Swal.fire({ icon: "warning", title: "Falta al menos el nombre de la persona", timer: 2200, showConfirmButton: false });
        return;
      }
      const entradaRenuente = {
        nombre: c.nombre,
        tipo_identificacion: c.tipo_identificacion || "N/A",
        numero_identificacion: c.numero_identificacion || `RENUENTE-${Date.now()}`,
        edad: c.edad || null, unidad_edad: c.edad ? c.unidad_edad : null,
        presenta_evidencia: "No",
        tiene_vacuna_sarampion: null, se_aplico_vacuna: null, motivo_no_vacunada: null,
        nombre_vacunador: null, nombre_biologico: null, lote: null,
        fotoA_base64: null, fotoB_base64: null, fotoPaiweb_base64: null,
      };
      if (editandoCarnetIdx !== null) {
        setForm(p => {
          const carnets = [...p.pai_vacunacion.carnets];
          carnets[editandoCarnetIdx] = entradaRenuente;
          return { ...p, pai_vacunacion: { ...p.pai_vacunacion, carnets } };
        });
        setEditandoCarnetIdx(null);
      } else {
        setForm(p => ({ ...p, pai_vacunacion: { ...p.pai_vacunacion, carnets: [...p.pai_vacunacion.carnets, entradaRenuente] } }));
      }
      setNuevoCarnet(CARNET_VACIO);
      return;
    }

    if (!c.nombre || !c.tipo_identificacion || !c.numero_identificacion || !c.edad) {
      Swal.fire({ icon: "warning", title: "Completa nombre, documento y edad de la persona", timer: 2200, showConfirmButton: false });
      return;
    }
    if (!c.presenta_evidencia) {
      Swal.fire({ icon: "warning", title: "Falta responder si presenta carnet o validación PAIWEB", timer: 2200, showConfirmButton: false });
      return;
    }
    if (c.presenta_evidencia === "Si") {
      // Las fotos del carnet (cara A/B o PAIWEB) ya no bloquean poder
      // agregar a la persona — mismo motivo que la foto de la vivienda:
      // en algunos celulares el navegador no logra procesar la foto, y
      // eso no debería impedir seguir con el resto del registro.
      if (!c.tiene_vacuna_sarampion) {
        Swal.fire({ icon: "warning", title: "Falta responder si ya tiene la vacuna de sarampión", timer: 2200, showConfirmButton: false });
        return;
      }
      if (c.tiene_vacuna_sarampion === "No") {
        if (!c.se_aplico_vacuna) {
          Swal.fire({ icon: "warning", title: "Falta responder si se le aplicó la vacuna ahora", timer: 2200, showConfirmButton: false });
          return;
        }
        if (c.se_aplico_vacuna === "Si" && (!c.nombre_vacunador || !c.nombre_biologico || !c.lote)) {
          Swal.fire({ icon: "warning", title: "Faltan datos de la vacunación: vacunador, biológico y lote", timer: 2200, showConfirmButton: false });
          return;
        }
        if (c.se_aplico_vacuna === "No" && !c.motivo_no_vacunada) {
          Swal.fire({ icon: "warning", title: "Falta el motivo por el que no se vacunó", timer: 2200, showConfirmButton: false });
          return;
        }
      }
    }

    const entrada = {
      nombre: c.nombre, tipo_identificacion: c.tipo_identificacion, numero_identificacion: c.numero_identificacion,
      edad: c.edad, unidad_edad: c.unidad_edad, presenta_evidencia: c.presenta_evidencia,
      tiene_vacuna_sarampion: c.presenta_evidencia === "Si" ? c.tiene_vacuna_sarampion : null,
      se_aplico_vacuna: c.tiene_vacuna_sarampion === "No" ? c.se_aplico_vacuna : null,
      motivo_no_vacunada: c.se_aplico_vacuna === "No" ? c.motivo_no_vacunada : null,
      nombre_vacunador: c.se_aplico_vacuna === "Si" ? c.nombre_vacunador : null,
      nombre_biologico: c.se_aplico_vacuna === "Si" ? c.nombre_biologico : null,
      lote: c.se_aplico_vacuna === "Si" ? c.lote : null,
      fotoA_base64: c.fotoA?.base64 || null, fotoB_base64: c.fotoB?.base64 || null, fotoPaiweb_base64: c.fotoPaiweb?.base64 || null,
    };

    if (editandoCarnetIdx !== null) {
      const numeroAnterior = form.pai_vacunacion.carnets[editandoCarnetIdx]?.numero_identificacion;
      setForm(p => {
        const carnets = [...p.pai_vacunacion.carnets];
        carnets[editandoCarnetIdx] = entrada;
        // Si cambió el documento y ya tenía un MRCV diligenciado, se
        // actualiza el vínculo para no dejarlo huérfano.
        const mrcv = p.mrcv.map(m => m.documento_nino === numeroAnterior
          ? { ...m, documento_nino: c.numero_identificacion, nombre_nino: c.nombre }
          : m);
        return { ...p, pai_vacunacion: { ...p.pai_vacunacion, carnets }, mrcv };
      });
      setEditandoCarnetIdx(null);
    } else {
      setForm(p => ({
        ...p,
        pai_vacunacion: { ...p.pai_vacunacion, carnets: [...p.pai_vacunacion.carnets, entrada] },
      }));
    }

    if (c.presenta_evidencia === "Si") {
      if (c.fotoA) await guardarFotoOffline(codigo, c.fotoA.base64, c.fotoA.nombre, "carnet_a", c.numero_identificacion);
      if (c.fotoB) await guardarFotoOffline(codigo, c.fotoB.base64, c.fotoB.nombre, "carnet_b", c.numero_identificacion);
      if (c.fotoPaiweb) await guardarFotoOffline(codigo, c.fotoPaiweb.base64, c.fotoPaiweb.nombre, "carnet_paiweb", c.numero_identificacion);
      // Mismo criterio que la foto de vivienda: solo intentar ya si la
      // visita ya existe en el servidor (editando una existente).
      if (visitaEditar) sincronizarFotosBac().catch(() => {});
    }

    setNuevoCarnet(CARNET_VACIO);
  };

  const editarPersonaCarnet = (idx) => {
    const c = form.pai_vacunacion.carnets[idx];
    const eraRenuente = c.presenta_evidencia !== "Si" && (c.tipo_identificacion === "N/A" || String(c.numero_identificacion || "").startsWith("RENUENTE-"));
    setNuevoCarnet({
      nombre: c.nombre,
      tipo_identificacion: eraRenuente ? "" : c.tipo_identificacion,
      numero_identificacion: eraRenuente ? "" : c.numero_identificacion,
      edad: c.edad, unidad_edad: c.unidad_edad, presenta_evidencia: c.presenta_evidencia,
      esRenuente: eraRenuente,
      tiene_vacuna_sarampion: c.tiene_vacuna_sarampion || "", se_aplico_vacuna: c.se_aplico_vacuna || "",
      motivo_no_vacunada: c.motivo_no_vacunada || "", nombre_vacunador: c.nombre_vacunador || "",
      nombre_biologico: c.nombre_biologico || "", lote: c.lote || "",
      fotoA: c.fotoA_base64 ? { base64: c.fotoA_base64, nombre: "carnet_a" } : null,
      fotoB: c.fotoB_base64 ? { base64: c.fotoB_base64, nombre: "carnet_b" } : null,
      fotoPaiweb: c.fotoPaiweb_base64 ? { base64: c.fotoPaiweb_base64, nombre: "carnet_paiweb" } : null,
    });
    setEditandoCarnetIdx(idx);
  };

  const cancelarEdicionCarnet = () => {
    setNuevoCarnet(CARNET_VACIO);
    setEditandoCarnetIdx(null);
  };

  const quitarCarnet = (idx) => {
    if (editandoCarnetIdx === idx) cancelarEdicionCarnet();
    setForm(p => ({ ...p, pai_vacunacion: { ...p.pai_vacunacion, carnets: p.pai_vacunacion.carnets.filter((_, i) => i !== idx) } }));
  };

  // Personas ≤5 años con evidencia — son las que necesitan su MRCV
  // antes de poder avanzar del paso 11.
  const pendientesMrcv = form.pai_vacunacion.carnets.filter(c => c.presenta_evidencia === "Si" && esMenorDe5(c));
  const siguientePendienteMrcv = pendientesMrcv.find(c => !form.mrcv.some(m => m.documento_nino === c.numero_identificacion));
  // Menores ≤5 SIN carnet — no se les puede hacer MRCV ahora, necesitan
  // que se les vuelva a visitar puntualmente para pedirles el carnet.
  const menoresSinCarnetParaMrcv = form.pai_vacunacion.carnets.filter(c => c.presenta_evidencia !== "Si" && esMenorDe5(c));

  useEffect(() => {
    setFormMrcv(MRCV_VACIO);
    setVacunasMrcv({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siguientePendienteMrcv?.numero_identificacion]);

  const guardarMrcvActual = () => {
    if (!siguientePendienteMrcv) return;
    if (!formMrcv.fecha_encuesta || !formMrcv.fecha_nacimiento) {
      Swal.fire({ icon: "warning", title: "Falta la fecha de encuesta o la fecha de nacimiento", timer: 2200, showConfirmButton: false });
      return;
    }
    if (!formMrcv.nombre_madre || !formMrcv.documento_madre) {
      Swal.fire({ icon: "warning", title: "Faltan nombre y documento de la madre o acudiente", timer: 2200, showConfirmButton: false });
      return;
    }
    if (!formMrcv.regimen || !formMrcv.aseguradora) {
      Swal.fire({ icon: "warning", title: "Faltan régimen y EPS", timer: 2200, showConfirmButton: false });
      return;
    }
    const entrada = {
      nombre_nino: siguientePendienteMrcv.nombre,
      documento_nino: siguientePendienteMrcv.numero_identificacion,
      ...formMrcv,
    };
    MRCV_VACUNAS.forEach(({ clave }) => {
      entrada[`${clave}_fecha`] = vacunasMrcv[clave]?.fecha || null;
      entrada[`${clave}_verificado`] = vacunasMrcv[clave]?.verificado || null;
    });
    setForm(p => ({ ...p, mrcv: [...p.mrcv, entrada] }));
  };

  // Guarda lo que se llenó en el modal exclusivo de MRCV sin carnet —
  // mismo arreglo form.mrcv que usan los que sí tienen carnet, para que
  // cuando se envíe la visita, todo salga junto en el mismo lote.
  const guardarMrcvSinCarnet = (entrada) => {
    setForm(p => ({ ...p, mrcv: [...p.mrcv, entrada] }));
    setMenorMrcvSinCarnet(null);
  };

  const guardarMrcvConCarnet = (entrada) => {
    setForm(p => ({ ...p, mrcv: [...p.mrcv, entrada] }));
    setMenorMrcvConCarnet(null);
  };

  const quitarMrcv = (idx) => {
    setForm(p => ({ ...p, mrcv: p.mrcv.filter((_, i) => i !== idx) }));
  };

  const agregarIntegrante = () => {
    if (!nuevoIntegrante.nombre || !nuevoIntegrante.edad) {
      Swal.fire({ icon: "warning", title: "Falta nombre o edad", timer: 1600, showConfirmButton: false });
      return;
    }
    setForm(p => ({ ...p, integrantes: [...p.integrantes, { ...nuevoIntegrante }] }));
    setNuevoIntegrante({ nombre: "", edad: "", unidad: "Años" });
  };
  const quitarIntegrante = (idx) => {
    setForm(p => ({ ...p, integrantes: p.integrantes.filter((_, i) => i !== idx) }));
  };

  // ── Contactos (rama CONTACTOS del formulario original) ──────────────
  const agregarContacto = () => {
    const c = nuevoContacto;
    const esIndigena = c.pertenencia_etnica === "Indígena";
    if (!c.nombre_apellidos || !c.tipo_identificacion || !c.documento_identidad
      || !c.grupo_poblacional || !c.pertenencia_etnica || !c.contacto_caso_sospechoso
      || !c.persona_enferma || (esIndigena && !c.etnia_indigena)) {
      Swal.fire({ icon: "warning", title: "Completa los campos obligatorios del contacto", timer: 2000, showConfirmButton: false });
      return;
    }
    setForm(p => ({ ...p, contactos: [...p.contactos, c] }));
    setNuevoContacto({
      nombre_apellidos: "", tipo_identificacion: "", documento_identidad: "",
      direccion: "", eapb: "", otro_eapb: "", grupo_poblacional: "",
      pertenencia_etnica: "", etnia_indigena: "", persona_enferma: "", contacto_caso_sospechoso: "", observacion: "",
    });
  };
  const quitarContacto = (idx) => {
    setForm(p => ({ ...p, contactos: p.contactos.filter((_, i) => i !== idx) }));
  };

  // ── PAI — Vacunación (paso 10) ───────────────────────────────────────
  const setPai = (grupo, campo, valor) => {
    setForm(p => ({
      ...p,
      pai_vacunacion: { ...p.pai_vacunacion, [grupo]: { ...p.pai_vacunacion[grupo], [campo]: valor } },
    }));
  };
  // Para biologico_pendiente / biologico_pendiente_detalle — van directo
  // en pai_vacunacion, no anidados dentro de un grupo de edad.
  const setPaiDirecto = (campo, valor) => setForm(p => ({ ...p, pai_vacunacion: { ...p.pai_vacunacion, [campo]: valor } }));
  const totalPaiGrupo = (grupo) =>
    Object.values(form.pai_vacunacion[grupo] || {}).reduce((acc, v) => acc + (parseInt(v, 10) || 0), 0);

  // Convierte los strings del formulario a números y calcula el Total de
  // cada grupo, listo para guardar — se hace solo al enviar, no en cada tecla.
  const construirPaiParaGuardar = () => {
    const resultado = {};
    // La matriz por grupos de edad YA NO se llena a mano ni se envía —
    // el backend la calcula sola a partir de "carnets" (ver
    // calcularMatrizPai.js). Lo único que se manda es lo que de verdad
    // se preguntó en campo.
    resultado.biologico_pendiente = form.pai_vacunacion.biologico_pendiente;
    resultado.biologico_pendiente_detalle = form.pai_vacunacion.biologico_pendiente_detalle || null;
    resultado.carnets = form.pai_vacunacion.carnets.map(c => ({
      nombre: c.nombre, tipo_identificacion: c.tipo_identificacion, numero_identificacion: c.numero_identificacion,
      edad: c.edad, unidad_edad: c.unidad_edad, presenta_evidencia: c.presenta_evidencia,
      tiene_vacuna_sarampion: c.tiene_vacuna_sarampion, se_aplico_vacuna: c.se_aplico_vacuna,
      motivo_no_vacunada: c.motivo_no_vacunada, nombre_vacunador: c.nombre_vacunador,
      nombre_biologico: c.nombre_biologico, lote: c.lote,
    }));
    return resultado;
  };

  // ── Validación POR PASO (igual criterio que DiscapacidadModal) ──────
  const validarPaso = (p) => {
    const e = {};

    if (p === 1) {
      if (enJornada && !jornada?.codigo) e.lider_bac = "No hay jornada abierta en esta comunidad — ábrela en la pestaña Jornadas";
      else if (!enJornada && !form.lider_bac) e.lider_bac = "Requerido";
    }

    if (p === 2) {
      if (!geoCapturada) e.geo = "Debes capturar la ubicación GPS";
      if (!form.direccion) e.direccion = "Requerido";
    }

    if (p === 3) {
      if (!form.tipo_fisico_vivienda) e.tipo_fisico_vivienda = "Requerido";
      if (!form.estado_vivienda) e.estado_vivienda = "Requerido";
      if (form.estado_vivienda === "No efectiva" && !form.motivo_no_efectiva) e.motivo_no_efectiva = "Requerido";
      if (requiereRevisitaPanel && !form.fecha_revisita_sugerida) e.fecha_revisita_sugerida = "Requerido";
    }

    if ((p === 4 || p === 12) && !fotoLocal) {
      e.foto = "La foto es obligatoria";
    }

    if (p === 5 && esEfectiva) {
      if (!form.nombre_entrevistado)      e.nombre_entrevistado = "Requerido";
      if (!form.tipo_documento)           e.tipo_documento = "Requerido";
      if (!form.documento_identificacion) e.documento_identificacion = "Requerido";
      if (!form.genero)                   e.genero = "Requerido";
      if (!form.eps)                      e.eps = "Requerido";
      if (form.eps === "Otro" && !form.otra_eps) e.otra_eps = "Requerido";
      if (!form.regimen_afiliacion) e.regimen_afiliacion = "Requerido";
      if (!form.grupo_poblacional)        e.grupo_poblacional = "Requerido";
      if (!form.pertenencia_etnica)       e.pertenencia_etnica = "Requerido";
    }

    if (p === 6 && esEfectiva) {
      if (!form.numero_personas_entrevistadas) e.numero_personas_entrevistadas = "Requerido";
      if (form.integrantes.length === 0) e.numero_personas_vivienda = "Agrega al menos un integrante — no puede quedar en 0";
    }

    if (p === 7 && esEfectiva) {
      if (!form.eisp_encontrado) e.eisp_encontrado = "Requerido";
      if (form.eisp_encontrado === "Si" && !form.sintomas_observados) e.sintomas_observados = "Requerido";
    }

    if (p === 8 && esEfectiva && form.eisp_encontrado === "Si") {
      if (form.contactos.length === 0) e.contactos = "Agrega al menos un contacto antes de continuar";
    }

    if (p === 9 && esEfectiva) {
      if (!(form.medios_informacion || []).length) e.medios_informacion = "Marca al menos una opción";
      if (!form.sabe_vacunacion) e.sabe_vacunacion = "Requerido";
      if (!form.conoce_personajes_vacunacion) e.conoce_personajes_vacunacion = "Requerido";
    }

    if (p === 10 && esEfectiva) {
      const totalVivienda = parseInt(form.numero_personas_vivienda, 10) || 0;
      if (form.pai_vacunacion.carnets.length !== totalVivienda) {
        e.carnets = `Faltan personas por caracterizar: hay ${totalVivienda} en la vivienda y llevas ${form.pai_vacunacion.carnets.length}.`;
      }
      if (form.pai_vacunacion.biologico_pendiente === "Si" && !form.pai_vacunacion.biologico_pendiente_detalle) {
        e.biologico_pendiente_detalle = "Indica cuál biológico está pendiente";
      }
    }

    if (p === 11 && esEfectiva && pendientesMrcv.length > 0) {
      if (form.mrcv.length < pendientesMrcv.length) {
        e.mrcv = `Faltan ${pendientesMrcv.length - form.mrcv.length} niño(s) por diligenciar en MRCV`;
      }
    }

    if (p === 11 && esEfectiva && menoresSinCarnetParaMrcv.length > 0) {
      const completadosSinCarnet = menoresSinCarnetParaMrcv.filter(c =>
        form.mrcv.some(m => m.documento_nino === c.numero_identificacion && m.tiene_carnet === "NO")
      ).length;
      if (completadosSinCarnet < menoresSinCarnetParaMrcv.length) {
        e.mrcv = `Faltan ${menoresSinCarnetParaMrcv.length - completadosSinCarnet} niño(s) sin carnet por diligenciar en MRCV`;
      }
    }

    return e;
  };

  // Determina si un paso aplica según las respuestas ya dadas — se usa
  // para saltarlo automáticamente al navegar (Anterior/Siguiente).
  const pasoAplica = (p) => {
    // La foto de la vivienda vive en 2 lugares posibles, nunca los 2 a
    // la vez: paso 4 para "No efectiva" (se pide temprano, ya que para
    // ellas casi no hay más pasos después), paso 12 para "Efectiva" (se
    // pide al final, como último paso, separado del contenido de MRCV).
    if (p === 4)  return !esEfectiva;
    if (p === 12) return esEfectiva;
    if ([5, 6, 7].includes(p)) return esEfectiva;
    if (p === 8) return esEfectiva && form.eisp_encontrado === "Si";
    if (p === 10) return esEfectiva;
    if (p === 11) return esEfectiva;
    return true;
  };

  const irSiguiente = () => {
    const e = validarPaso(step);
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    let siguiente = step + 1;
    while (siguiente < TOTAL_PASOS && !pasoAplica(siguiente)) siguiente++;
    setStep(Math.min(siguiente, TOTAL_PASOS));
  };
  const irAnterior = () => {
    let anterior = step - 1;
    while (anterior > 1 && !pasoAplica(anterior)) anterior--;
    setStep(Math.max(anterior, 1));
  };

  // ── Alerta "Guardando... X%" con progreso real de subida ────────────
  // Con fotos de vivienda + carnets + esquema de vacunación, el payload
  // puede pesar bastante — esto le muestra a la persona que sí está
  // avanzando, en vez de dejarla mirando "Guardando..." sin saber si
  // sirvió de algo (importante en conexiones lentas).
  const abrirAlertaGuardando = () => {
    Swal.fire({
      title: "Guardando...",
      html: `
        <div style="font-size:12.5px;color:#6b7280;margin-bottom:10px">Enviando la información al servidor — no cierres esta ventana.</div>
        <div style="background:#e5e7eb;border-radius:20px;height:14px;overflow:hidden;">
          <div id="swal-progreso-barra-bac" style="background:linear-gradient(135deg,#16342a,#2d6a4f);height:100%;width:0%;transition:width .2s;"></div>
        </div>
        <div id="swal-progreso-texto-bac" style="font-size:13px;font-weight:700;color:#16342a;margin-top:8px;">0%</div>
      `,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
    });
  };
  const actualizarProgresoAlerta = (pct) => {
    const barra = document.getElementById("swal-progreso-barra-bac");
    const texto = document.getElementById("swal-progreso-texto-bac");
    if (barra) barra.style.width = `${pct}%`;
    if (texto) texto.textContent = `${pct}%`;
  };

  const handleGuardar = async () => {
    const e = validarPaso(step);
    if (Object.keys(e).length) { setErrors(e); return; }
    setGuardando(true);
    try {
      const payload = {
        ...form,
        codigo_vivienda: modoRevisita ? undefined : codigo,
        codigo_vivienda_previo: modoRevisita ? codigo : undefined,
        territorio: ctx?.territorio,
        comunidad:  ctx?.comunidad,
        numero_personas_entrevistadas: form.numero_personas_entrevistadas ? parseInt(form.numero_personas_entrevistadas) : null,
        numero_personas_vivienda: form.numero_personas_vivienda ? parseInt(form.numero_personas_vivienda) : null,
        // Un campo DATE en MySQL no acepta "" (cadena vacía) — Sequelize la
        // intenta convertir con `new Date("")`, que da "Invalid Date", y eso
        // es justo lo que se estaba guardando literal en la base. Si no se
        // programó revisita, debe viajar como null, nunca como "".
        fecha_revisita_sugerida: form.fecha_revisita_sugerida || null,
        medios_informacion: esEfectiva ? (form.medios_informacion || []) : [],
        sabe_vacunacion: esEfectiva ? (form.sabe_vacunacion || null) : null,
        conoce_personajes_vacunacion: esEfectiva ? (form.conoce_personajes_vacunacion || null) : null,
        ...(enJornada ? { lider_bac: jornada?.lider_nombre || form.lider_bac, jornada_codigo: jornada?.codigo || null } : {}),
        pai_vacunacion: esEfectiva ? construirPaiParaGuardar() : null,
        mrcv: esEfectiva ? form.mrcv : [],
      };

      // ── Edición de un borrador que TODAVÍA no ha sincronizado — se
      //    actualiza directo en el dispositivo, sin necesitar internet
      //    para nada. Cuando le toque sincronizar (solo, más adelante),
      //    sale ya con los datos corregidos.
      if (visitaEditar?._pendiente) {
        const resultado = await actualizarBorradorPendiente(visitaEditar._localId, payload);
        if (!resultado.ok) {
          Swal.fire({
            icon: "info",
            title: "Este registro ya se sincronizó",
            text: "Se guardó en el servidor justo mientras lo editabas, así que esta edición no se guardó — ábrelo de nuevo desde la tabla para editarlo con conexión, como cualquier registro ya sincronizado.",
            confirmButtonColor: "#16342a",
          });
          setGuardando(false);
          await reload();
          onClose();
          return;
        }
        Swal.fire({ icon: "success", title: "Borrador actualizado", timer: 1600, showConfirmButton: false });
        limpiarBorrador();
        await reload();
        onClose();
        setGuardando(false);
        return;
      }

      // ── Edición (admin) — actualiza el registro existente, no crea uno
      //    nuevo. Exige conexión (es una acción administrativa puntual,
      //    no el flujo normal de campo que sí necesita funcionar offline).
      if (visitaEditar) {
        if (!online) {
          Swal.fire("Sin conexión", "Editar un registro requiere conexión a internet.", "warning");
          setGuardando(false);
          return;
        }
        abrirAlertaGuardando();
        const config = { onUploadProgress: (evt) => { if (evt.total) actualizarProgresoAlerta(Math.min(99, Math.round((evt.loaded * 100) / evt.total))); } };
        await updateVisitaBac(visitaEditar.id, payload, config);
        if (esEfectiva && visitaEditar.pai_vacunacion?.id) {
          await editarPaiVacunacion(visitaEditar.pai_vacunacion.id, payload.pai_vacunacion);
        }
        actualizarProgresoAlerta(100);
        Swal.close();
        Swal.fire({ icon: "success", title: "Cambios guardados", timer: 1800, showConfirmButton: false });
        limpiarBorrador();
        await reload();
        onClose();
        setGuardando(false);
        return;
      }

      if (!online) {
        await guardarOfflineBacVisitaCrear(payload);
        limpiarBorrador();
        Swal.fire({ icon: "info", title: "Guardado offline", text: "Se sincronizará al recuperar internet.", confirmButtonColor: "#16342a", timer: 2200, showConfirmButton: false });
        await reload();
        onClose();
        return;
      }

      try {
        abrirAlertaGuardando();
        const config = { onUploadProgress: (evt) => { if (evt.total) actualizarProgresoAlerta(Math.min(99, Math.round((evt.loaded * 100) / evt.total))); } };
        await createVisitaBac(payload, config);
        actualizarProgresoAlerta(100);
        Swal.close();
        // La vivienda YA existe en el servidor — este es el momento
        // exacto en que una foto tomada antes (cuando la visita todavía
        // no existía) puede terminar de adjuntarse. Sin este llamado, se
        // quedaría esperando indefinidamente al próximo evento "online",
        // que puede tardar en llegar.
        sincronizarFotosBac().catch(() => {});
        limpiarBorrador();
        await reload();
        onClose();
      } catch (errorEnLinea) {
        Swal.close();
        const status = errorEnLinea.response?.status;
        if (status && CODIGOS_ERROR_DE_DATOS.includes(status)) throw errorEnLinea;
        console.warn("⚠️ No se pudo guardar en línea, se guarda offline como respaldo:", errorEnLinea.message);
        await guardarOfflineBacVisitaCrear(payload);
        limpiarBorrador();
        Swal.fire({ icon: "info", title: "Guardado offline (respaldo)", text: "El servidor no respondió. Tus datos quedaron en este dispositivo y se sincronizarán solos.", confirmButtonColor: "#16342a" });
        await reload();
        onClose();
      }
    } catch (e2) {
      Swal.fire("Error", e2.response?.data?.message || "No se pudo guardar", "error");
    } finally {
      setGuardando(false);
    }
  };

  const handleClose = async () => {
    const result = await Swal.fire({
      title: "¿Salir del formulario?", text: "El progreso no guardado se perderá.",
      icon: "warning", showCancelButton: true,
      confirmButtonColor: "#b3261e", cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, salir", cancelButtonText: "Cancelar",
    });
    if (result.isConfirmed) onClose();
  };

  if (!show) return null;

  const opcionesSelect = (arr) => arr.map(v => ({ value: v, label: v }));
  const valorSelect = (v) => (v ? { value: v, label: v } : null);

  const ESTADOS = [
    { v: "Efectiva",     dot: "#2d6a4f" },
    { v: "No efectiva",  dot: "#a15c9e" },
  ];

  return (
    <>
    <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false} size="lg" centered scrollable dialogClassName="bac-modal-wizard">
      <style>{`
        /* Evita que el navegador haga zoom automático al enfocar un campo
           en celular — pasa cuando el texto es menor a 16px. Aplica a
           inputs normales, Bootstrap y también al buscador interno de
           react-select (que por dentro es un <input> real). */
        .bac-modal-wizard input,
        .bac-modal-wizard select,
        .bac-modal-wizard textarea,
        .bac-modal-wizard .form-control,
        .bac-modal-wizard .form-select {
          font-size: 16px !important;
        }
      `}</style>
      <Modal.Header closeButton style={{ background: "linear-gradient(135deg, #16342a, #2d6a4f)", color: "#fff" }}>
        <Modal.Title style={{ fontSize: "1rem" }}>
          {visitaEditar ? "✏️ Editar visita" : modoRevisita ? "🔁 Revisitar vivienda" : "Nueva visita"} — BAC Sarampión
          {!online && <span className="badge bg-warning text-dark ms-2" style={{ fontSize: "0.7rem" }}>📴 Offline</span>}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {cargandoCatalogos ? (
          <p className="text-center text-muted py-4">Cargando listas...</p>
        ) : (
          <>
            {/* Barra de progreso */}
            <div className="mb-4">
              <div style={{ height: 8, background: "#e7ebf0", borderRadius: 20 }}>
                <div style={{ width: `${(step / TOTAL_PASOS) * 100}%`, height: 8, background: "#2d6a4f", borderRadius: 20, transition: "width 0.3s" }} />
              </div>
              <p className="mt-2 text-muted small mb-0">Paso {step} de {TOTAL_PASOS} — {TITULOS_PASO[step]}</p>
            </div>

            {/* Identificador — visible siempre, no es un paso en sí */}
            <div className="d-flex justify-content-between align-items-center gap-3 p-2 px-3 rounded mb-4" style={{ background: "#f5f6f0", border: "1px dashed #4d8f6f" }}>
              <div>
                <div style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 14, color: "#2d6a4f" }}>{codigo}</div>
                <div className="text-muted" style={{ fontSize: 11 }}>
                  {modoRevisita ? `Intento #${(viviendaBase?.numero_visita || 1) + 1}` : "Identificador de esta vivienda"}
                </div>
              </div>
            </div>

            <Form>
              {/* ═══ PASO 1 — Identificación ═══════════════════════════ */}
              {step === 1 && (
                <>
                  <h5 className="mb-3">1. Identificación de la visita</h5>
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <Form.Label>{req("Líder de la BAC")}</Form.Label>
                      {enJornada ? (
                        <>
                          <Form.Control value={jornada?.lider_nombre || "— sin jornada abierta —"} disabled plaintext
                            style={{ background: "#f8f9fa", fontWeight: 600, paddingLeft: 12, borderRadius: 6 }} />
                          <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                            {jornada ? `Jornada BAC desde ${String(jornada.fecha_inicio || "").slice(0, 10).split("-").reverse().join("/")} — el líder se asigna solo.` : ""}
                          </div>
                          {errors.lider_bac && <div className="text-danger mt-1" style={{ fontSize: "0.8em" }}>{errors.lider_bac}</div>}
                        </>
                      ) : (<>
                      <Select
                        placeholder="Buscar líder..."
                        options={opcionesSelect(opcionesDe("responsables-bac"))}
                        value={valorSelect(form.lider_bac)}
                        onChange={(sel) => set("lider_bac", sel?.value || "")}
                        isClearable menuPlacement="auto" menuPosition="fixed"
                        styles={selectStyles} esError={!!errors.lider_bac}
                      />
                      {errors.lider_bac && <div className="text-danger mt-1" style={{ fontSize: "0.8em" }}>{errors.lider_bac}</div>}
                      </>)}
                    </div>
                    <div className="col-12 col-md-6">
                      <Form.Label>Entrevistador</Form.Label>
                      <Form.Control value={form.entrevistador} disabled plaintext
                        style={{ background: "#f8f9fa", fontWeight: 600, paddingLeft: 12, borderRadius: 6 }} />
                      <div className="text-muted mt-1" style={{ fontSize: 11 }}>Se llena solo con tu usuario — no se puede cambiar.</div>
                    </div>
                  </div>
                </>
              )}

              {/* ═══ PASO 2 — Ubicación ═══════════════════════════════ */}
              {step === 2 && (
                <>
                  <h5 className="mb-3">2. Ubicación</h5>
                  {!geoCapturada ? (
                    <div className="d-flex align-items-center gap-3 p-3 rounded mb-2" style={{ background: "#f8e2df", border: `1px solid ${errors.geo ? "#b3261e" : "rgba(179,38,30,.3)"}` }}>
                      <div className="flex-grow-1" style={{ fontSize: 13 }}>
                        <strong>Ubicación no capturada</strong>
                        <div className="text-muted">Obligatorio para poder guardar la visita.</div>
                      </div>
                      <Button onClick={capturarGPS} disabled={gpsCapturando} style={{ background: "#16342a", border: "none", whiteSpace: "nowrap" }}>
                        {gpsCapturando ? "Obteniendo…" : "📍 Capturar GPS"}
                      </Button>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center gap-3 p-3 rounded mb-2" style={{ background: "#e4f2e8", border: "1px solid rgba(45,106,79,.3)" }}>
                      <div className="flex-grow-1" style={{ fontSize: 13 }}>
                        <strong>Ubicación capturada</strong>
                        <div className="text-muted" style={{ fontFamily: "monospace" }}>
                          {Number(form.lat).toFixed(5)}, {Number(form.lng).toFixed(5)} {form.precision_gps ? `· ± ${Math.round(form.precision_gps)} m` : ""}
                        </div>
                      </div>
                      <Button variant="outline-secondary" onClick={capturarGPS}>Actualizar</Button>
                    </div>
                  )}
                  {errors.geo && <div className="text-danger mb-2" style={{ fontSize: "0.8em" }}>{errors.geo}</div>}

                  <Form.Group className="mt-3">
                    <Form.Label>{req("Dirección / punto de referencia")}</Form.Label>
                    <Form.Control value={form.direccion} isInvalid={!!errors.direccion} onChange={e => set("direccion", e.target.value)}
                      placeholder="Ej: frente a la escuela, casa azul"
                      disabled={modoRevisita && !!viviendaBase?.direccion} />
                    <Form.Control.Feedback type="invalid">{errors.direccion}</Form.Control.Feedback>
                  </Form.Group>
                </>
              )}

              {/* ═══ PASO 3 — Vivienda ═════════════════════════════════ */}
              {step === 3 && (
                <>
                  <h5 className="mb-3">3. Vivienda</h5>
                  <Form.Group className="mb-3">
                    <Form.Label>{req("Tipo de vivienda")}</Form.Label>
                    <Form.Select value={form.tipo_fisico_vivienda} isInvalid={!!errors.tipo_fisico_vivienda}
                      onChange={e => set("tipo_fisico_vivienda", e.target.value)}>
                      <option value="">Seleccionar...</option>
                      {opcionesDe("tipos-vivienda").map(v => <option key={v} value={v}>{v}</option>)}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">{errors.tipo_fisico_vivienda}</Form.Control.Feedback>
                  </Form.Group>

                  <Form.Label>{req("Estado de la visita")}</Form.Label>
                  <div className="row g-2 mb-2">
                    {ESTADOS.map(op => (
                      <div className="col-12 col-sm-6" key={op.v}>
                        <label className="d-flex align-items-center gap-2 p-2 px-3 rounded w-100" style={{
                          cursor: "pointer",
                          border: `1.5px solid ${form.estado_vivienda === op.v ? "#16342a" : "#dee2e6"}`,
                          background: form.estado_vivienda === op.v ? "#f5f6f0" : "#fff",
                        }}>
                          <input type="radio" name="estado" className="d-none" checked={form.estado_vivienda === op.v}
                            onChange={() => setForm(p => ({ ...p, estado_vivienda: op.v, motivo_no_efectiva: "", fecha_revisita_sugerida: "", nota_revisita: "" }))} />
                          <span style={{ width: 10, height: 10, borderRadius: "50%", background: op.dot, flexShrink: 0 }} />
                          <span style={{ fontSize: 13 }}>{op.v}</span>
                        </label>
                      </div>
                    ))}
                  </div>

                  {form.estado_vivienda === "No efectiva" && (
                    <Form.Group className="mb-2">
                      <Form.Label>{req("Motivo de No Efectiva")}</Form.Label>
                      <Form.Select value={form.motivo_no_efectiva || ""}
                        onChange={e => setForm(p => ({ ...p, motivo_no_efectiva: e.target.value, fecha_revisita_sugerida: "", nota_revisita: "" }))}
                        isInvalid={!!errors.motivo_no_efectiva}>
                        <option value="">Selecciona...</option>
                        {["Renuente", "No habitada", "Abandona", "Menor en casa", "Cerrada"].map(m => <option key={m} value={m}>{m}</option>)}
                      </Form.Select>
                      {errors.motivo_no_efectiva && <div className="text-danger mt-1" style={{ fontSize: "0.8em" }}>{errors.motivo_no_efectiva}</div>}
                    </Form.Group>
                  )}

                  {requiereRevisitaPanel && (
                    <div className="p-3 rounded mt-2" style={{ background: "#faf0dc", border: "1px solid rgba(183,121,31,.3)" }}>
                      <div className="fw-bold mb-2" style={{ fontSize: 13, color: "#b7791f" }}>Programar revisita</div>
                      <div className="row g-3">
                        <div className="col-12 col-md-6">
                          <Form.Label>{req("Fecha sugerida")}</Form.Label>
                          <Form.Control type="date" isInvalid={!!errors.fecha_revisita_sugerida}
                            value={form.fecha_revisita_sugerida} onChange={e => set("fecha_revisita_sugerida", e.target.value)} />
                          <Form.Control.Feedback type="invalid">{errors.fecha_revisita_sugerida}</Form.Control.Feedback>
                        </div>
                        <div className="col-12 col-md-6">
                          <Form.Label>Nota para quien revisite</Form.Label>
                          <Form.Control value={form.nota_revisita} onChange={e => set("nota_revisita", e.target.value)} placeholder="Ej: volver después de las 4pm" />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* ═══ PASO 4 — Fotografía (solo No efectiva — Efectiva la
                    pide al final, junto con MRCV, paso 11) ═══════════ */}
              {step === 4 && (
                <>
                  <h5 className="mb-3">4. Fotografía de la vivienda <span style={{ color: "#b3261e" }}>*</span></h5>
                  <div className="d-flex align-items-center gap-3 p-3 rounded" style={{ background: "#f9fafb", border: `1px dashed ${errors.foto ? "#b3261e" : "#dee2e6"}` }}>
                    <div className="rounded d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 64, height: 64, background: "#eef1e7", overflow: "hidden" }}>
                      {fotoLocal ? <img src={fotoLocal.base64} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "🏠"}
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex gap-2 flex-wrap">
                        <label className="btn btn-outline-secondary btn-sm mb-0">
                          {comprimiendoFoto ? "Procesando…" : "📷 Tomar foto"}
                          <input type="file" accept="image/*" capture="environment" onChange={handleFoto} disabled={comprimiendoFoto} className="d-none" />
                        </label>
                        <label className="btn btn-outline-secondary btn-sm mb-0">
                          {comprimiendoFoto ? "Procesando…" : "🖼️ Elegir de galería"}
                          <input type="file" accept="image/*" onChange={handleFoto} disabled={comprimiendoFoto} className="d-none" />
                        </label>
                      </div>
                      <div className="text-muted mt-1" style={{ fontSize: 11.5 }}>
                        {fotoLocal
                          ? `💾 Guardada en el dispositivo (${fotoLocal.pesoOriginalKB}KB → ${fotoLocal.pesoFinalKB}KB) — se sincroniza sola, aparte de la visita`
                          : "Ningún archivo seleccionado"}
                      </div>
                    </div>
                  </div>
                  {errors.foto && <div className="text-danger mt-2" style={{ fontSize: "0.8em" }}>{errors.foto}</div>}
                </>
              )}

              {/* ═══ PASO 5 — Datos de contacto ════════════════════════ */}
              {step === 5 && (
                esEfectiva ? (
                  <>
                    <h5 className="mb-3">5. Datos de contacto</h5>
                    <div className="row g-3">
                      <div className="col-12">
                        <Form.Label>{req("Nombres y apellidos")}</Form.Label>
                        <Form.Control isInvalid={!!errors.nombre_entrevistado} value={form.nombre_entrevistado}
                          onChange={e => set("nombre_entrevistado", e.target.value.toUpperCase())} />
                        <Form.Control.Feedback type="invalid">{errors.nombre_entrevistado}</Form.Control.Feedback>
                      </div>
                      <div className="col-12 col-md-6">
                        <Form.Label>{req("Tipo de documento")}</Form.Label>
                        <Form.Select isInvalid={!!errors.tipo_documento} value={form.tipo_documento} onChange={e => set("tipo_documento", e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {opcionesDe("tipos-documento").map(v => <option key={v} value={v}>{v}</option>)}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{errors.tipo_documento}</Form.Control.Feedback>
                      </div>
                      <div className="col-12 col-md-6">
                        <Form.Label>{req("N° documento")}</Form.Label>
                        <Form.Control isInvalid={!!errors.documento_identificacion} value={form.documento_identificacion}
                          onChange={e => set("documento_identificacion", e.target.value)} />
                        <Form.Control.Feedback type="invalid">{errors.documento_identificacion}</Form.Control.Feedback>
                      </div>
                      <div className="col-12 col-md-6">
                        <Form.Label>{req("Género")}</Form.Label>
                        <Form.Select isInvalid={!!errors.genero} value={form.genero} onChange={e => set("genero", e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {opcionesDe("generos").map(v => <option key={v} value={v}>{v}</option>)}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{errors.genero}</Form.Control.Feedback>
                      </div>
                      <div className="col-12 col-md-6">
                        <Form.Label>Teléfono</Form.Label>
                        <Form.Control value={form.telefono} onChange={e => set("telefono", e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6">
                        <Form.Label>{req("EPS")}</Form.Label>
                        <Select
                          placeholder="Buscar EPS..."
                          options={opcionesSelect(opcionesDe("eps"))}
                          value={valorSelect(form.eps)}
                          onChange={(sel) => set("eps", sel?.value || "")}
                          isClearable menuPlacement="auto" menuPosition="fixed"
                          styles={selectStyles} esError={!!errors.eps}
                        />
                        {errors.eps && <div className="text-danger mt-1" style={{ fontSize: "0.8em" }}>{errors.eps}</div>}
                      </div>
                      {form.eps === "Otro" && (
                        <div className="col-12 col-md-6">
                          <Form.Label>{req("¿Cuál EPS?")}</Form.Label>
                          <Form.Control isInvalid={!!errors.otra_eps} value={form.otra_eps} onChange={e => set("otra_eps", e.target.value)} />
                          <Form.Control.Feedback type="invalid">{errors.otra_eps}</Form.Control.Feedback>
                        </div>
                      )}
                    </div>

                    <Form.Group className="mt-3">
                      <Form.Label>{req("Régimen de afiliación")}</Form.Label>
                      <Form.Select isInvalid={!!errors.regimen_afiliacion} value={form.regimen_afiliacion} onChange={e => set("regimen_afiliacion", e.target.value)}>
                        <option value="">Seleccionar...</option>
                        {opcionesDe("regimenes-afiliacion").map(v => <option key={v} value={v}>{v}</option>)}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">{errors.regimen_afiliacion}</Form.Control.Feedback>
                    </Form.Group>

                    <div className="row g-3 mt-1">
                      <div className="col-12 col-md-6">
                        <Form.Label>{req("Grupo poblacional")}</Form.Label>
                        <Form.Select isInvalid={!!errors.grupo_poblacional} value={form.grupo_poblacional} onChange={e => set("grupo_poblacional", e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {opcionesDe("grupos-poblacionales").map(v => <option key={v} value={v}>{v}</option>)}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{errors.grupo_poblacional}</Form.Control.Feedback>
                      </div>
                      <div className="col-12 col-md-6">
                        <Form.Label>{req("Enfoque diferencial")}</Form.Label>
                        <Form.Select isInvalid={!!errors.pertenencia_etnica} value={form.pertenencia_etnica} onChange={e => set("pertenencia_etnica", e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {ENFOQUE_DIFERENCIAL.map(v => <option key={v} value={v}>{v}</option>)}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{errors.pertenencia_etnica}</Form.Control.Feedback>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="alert alert-info py-2 px-3" style={{ fontSize: 13 }}>
                    ℹ️ No aplica — el estado de la visita no fue "Efectiva".
                  </div>
                )
              )}

              {/* ═══ PASO 6 — Composición del hogar ════════════════════ */}
              {step === 6 && (
                esEfectiva ? (
                  <>
                    <h5 className="mb-3">6. Composición del hogar</h5>
                    <div className="row g-3 mb-3">
                      <div className="col-12 col-md-6">
                        <Form.Label>{req("N° personas entrevistadas")}</Form.Label>
                        <Form.Control type="number" min={0} isInvalid={!!errors.numero_personas_entrevistadas}
                          value={form.numero_personas_entrevistadas} onChange={e => set("numero_personas_entrevistadas", e.target.value)} />
                        <Form.Control.Feedback type="invalid">{errors.numero_personas_entrevistadas}</Form.Control.Feedback>
                      </div>
                      <div className="col-12 col-md-6">
                        <Form.Label>N° personas en la vivienda <span className="text-muted" style={{ fontWeight: 400, fontSize: 11 }}>(automático)</span></Form.Label>
                        <Form.Control readOnly plaintext value={form.numero_personas_vivienda || "0"}
                          style={{ background: errors.numero_personas_vivienda ? "#f8e2df" : "#f8f9fa", fontWeight: 600, paddingLeft: 12, color: errors.numero_personas_vivienda ? "#b3261e" : "inherit" }} />
                        {errors.numero_personas_vivienda && <div className="text-danger mt-1" style={{ fontSize: "0.8em" }}>{errors.numero_personas_vivienda}</div>}
                      </div>
                    </div>

                    <Form.Label>Edad de los integrantes</Form.Label>
                    <div className="row g-2 align-items-end mb-3">
                      <div className="col-12 col-sm-5">
                        <Form.Control placeholder="Nombre" value={nuevoIntegrante.nombre}
                          onChange={e => setNuevoIntegrante(p => ({ ...p, nombre: e.target.value }))} />
                      </div>
                      <div className="col-6 col-sm-3">
                        <Form.Control type="number" min={0} placeholder="Edad" value={nuevoIntegrante.edad}
                          onChange={e => setNuevoIntegrante(p => ({ ...p, edad: e.target.value }))} />
                      </div>
                      <div className="col-6 col-sm-2">
                        <Form.Select value={nuevoIntegrante.unidad} onChange={e => setNuevoIntegrante(p => ({ ...p, unidad: e.target.value }))}>
                          <option value="Meses">Meses</option>
                          <option value="Años">Años</option>
                        </Form.Select>
                      </div>
                      <div className="col-12 col-sm-2 d-grid">
                        <Button variant="success" onClick={agregarIntegrante} type="button">+ Agregar</Button>
                      </div>
                    </div>

                    {form.integrantes.length > 0 && (
                      <div className="d-flex flex-column gap-2">
                        {form.integrantes.map((p, idx) => (
                          <div key={idx} className="d-flex justify-content-between align-items-center p-2 px-3 rounded" style={{ background: "#f5f6f0", fontSize: 13 }}>
                            <span><strong>{p.nombre}</strong> — {p.edad} {p.unidad}</span>
                            <button type="button" onClick={() => quitarIntegrante(idx)} className="btn btn-link text-danger p-0" style={{ fontSize: 14 }}>✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="alert alert-info py-2 px-3" style={{ fontSize: 13 }}>
                    ℹ️ No aplica — el estado de la visita no fue "Efectiva".
                  </div>
                )
              )}

              {/* ═══ PASO 7 — EISP ══════════════════════════════════════ */}
              {step === 7 && (
                esEfectiva ? (
                  <>
                    <h5 className="mb-3">7. EISP</h5>
                    <Form.Label>{req("¿EISP encontrado?")}</Form.Label>
                    <div className="d-flex gap-2 mb-2">
                      {["Si", "No"].map(v => (
                        <Button key={v} type="button" className="flex-grow-1 fw-bold"
                          variant={form.eisp_encontrado === v ? (v === "Si" ? "danger" : "success") : "outline-secondary"}
                          onClick={() => set("eisp_encontrado", v)}>
                          {v}
                        </Button>
                      ))}
                    </div>
                    {errors.eisp_encontrado && <div className="text-danger mb-2" style={{ fontSize: "0.8em" }}>{errors.eisp_encontrado}</div>}

                    {form.eisp_encontrado === "Si" && (
                      <>
                        <Form.Group className="mb-3">
                          <Form.Label>¿Cuál o cuáles?</Form.Label>
                          <Form.Control as="textarea" rows={2} isInvalid={!!errors.eisp_cual}
                            value={form.eisp_cual} onChange={e => set("eisp_cual", e.target.value)} />
                          <Form.Control.Feedback type="invalid">{errors.eisp_cual}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>{esEfectiva && form.eisp_encontrado === "Si" ? req("Síntomas observados") : "Síntomas observados"}</Form.Label>
                          <Form.Control as="textarea" rows={2} isInvalid={!!errors.sintomas_observados}
                            value={form.sintomas_observados} onChange={e => set("sintomas_observados", e.target.value)} />
                          <Form.Control.Feedback type="invalid">{errors.sintomas_observados}</Form.Control.Feedback>
                        </Form.Group>
                      </>
                    )}
                  </>
                ) : (
                  <div className="alert alert-info py-2 px-3" style={{ fontSize: 13 }}>
                    ℹ️ No aplica — el estado de la visita no fue "Efectiva".
                  </div>
                )
              )}

              {/* ═══ PASO 8 — Contactos ═════════════════════════════════ */}
              {step === 8 && (
                (esEfectiva && form.eisp_encontrado === "Si") ? (
                  <>
                    <h5 className="mb-3">8. Contactos</h5>
                    <p className="text-muted" style={{ fontSize: 12.5 }}>
                      Contactos del caso sospechoso/probable identificado en esta vivienda.
                    </p>

                    <div className="row g-3 mb-2">
                      <div className="col-12">
                        <Form.Label>{req("Nombre y apellidos")}</Form.Label>
                        <Form.Control value={nuevoContacto.nombre_apellidos}
                          onChange={e => setNuevoContacto(p => ({ ...p, nombre_apellidos: e.target.value }))} />
                      </div>
                      <div className="col-12 col-md-6">
                        <Form.Label>{req("Tipo de identificación")}</Form.Label>
                        <Form.Select value={nuevoContacto.tipo_identificacion}
                          onChange={e => setNuevoContacto(p => ({ ...p, tipo_identificacion: e.target.value }))}>
                          <option value="">Seleccionar...</option>
                          {opcionesDe("tipos-documento").map(v => <option key={v} value={v}>{v}</option>)}
                        </Form.Select>
                      </div>
                      <div className="col-12 col-md-6">
                        <Form.Label>{req("Documento de identidad")}</Form.Label>
                        <Form.Control value={nuevoContacto.documento_identidad}
                          onChange={e => setNuevoContacto(p => ({ ...p, documento_identidad: e.target.value }))} />
                      </div>
                      <div className="col-12">
                        <Form.Label>Dirección</Form.Label>
                        <Form.Control value={nuevoContacto.direccion}
                          onChange={e => setNuevoContacto(p => ({ ...p, direccion: e.target.value }))} />
                      </div>
                    </div>

                    <Form.Group className="mb-2">
                      <Form.Label>EAPB</Form.Label>
                      <Select
                        placeholder="Buscar EAPB..."
                        options={opcionesSelect(opcionesDe("eps"))}
                        value={valorSelect(nuevoContacto.eapb)}
                        onChange={(sel) => setNuevoContacto(p => ({ ...p, eapb: sel?.value || "" }))}
                        isClearable menuPlacement="auto" menuPosition="fixed"
                        styles={selectStyles}
                      />
                    </Form.Group>
                    {nuevoContacto.eapb === "Otro" && (
                      <Form.Group className="mb-2">
                        <Form.Label>¿Cuál EAPB?</Form.Label>
                        <Form.Control value={nuevoContacto.otro_eapb}
                          onChange={e => setNuevoContacto(p => ({ ...p, otro_eapb: e.target.value }))} />
                      </Form.Group>
                    )}

                    <Form.Group className="mb-2">
                      <Form.Label>{req("Grupo poblacional")}</Form.Label>
                      <Form.Select value={nuevoContacto.grupo_poblacional} onChange={e => setNuevoContacto(p => ({ ...p, grupo_poblacional: e.target.value }))}>
                        <option value="">Seleccionar...</option>
                        {opcionesDe("grupos-poblacionales-contacto").map(v => <option key={v} value={v}>{v}</option>)}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-2">
                      <Form.Label>{req("Enfoque diferencial")}</Form.Label>
                      <Form.Select value={nuevoContacto.pertenencia_etnica} onChange={e => setNuevoContacto(p => ({ ...p, pertenencia_etnica: e.target.value }))}>
                        <option value="">Seleccionar...</option>
                        {ENFOQUE_DIFERENCIAL.map(v => <option key={v} value={v}>{v}</option>)}
                      </Form.Select>
                    </Form.Group>

                    {nuevoContacto.pertenencia_etnica === "Indígena" && (
                      <Form.Group className="mb-2">
                        <Form.Label>{req("Etnia")}</Form.Label>
                        <Select
                          placeholder="Buscar etnia..."
                          options={opcionesSelect(opcionesDe("etnias"))}
                          value={valorSelect(nuevoContacto.etnia_indigena)}
                          onChange={(sel) => setNuevoContacto(p => ({ ...p, etnia_indigena: sel?.value || "" }))}
                          isClearable menuPlacement="auto" menuPosition="fixed"
                          styles={selectStyles}
                        />
                      </Form.Group>
                    )}

                    <Form.Group className="mb-2">
                      <Form.Label>{req("¿La persona se encuentra enferma?")}</Form.Label>
                      <div className="d-flex gap-2">
                        {["SI", "NO"].map(v => (
                          <Button key={v} type="button" className="flex-grow-1"
                            variant={nuevoContacto.persona_enferma === v ? (v === "SI" ? "danger" : "success") : "outline-secondary"}
                            onClick={() => setNuevoContacto(p => ({ ...p, persona_enferma: v }))}>
                            {v}
                          </Button>
                        ))}
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-2">
                      <Form.Label>{req("Contacto de un caso sospechoso/probable")}</Form.Label>
                      <div className="d-flex gap-2">
                        {["SI", "NO"].map(v => (
                          <Button key={v} type="button" className="flex-grow-1"
                            variant={nuevoContacto.contacto_caso_sospechoso === v ? "primary" : "outline-secondary"}
                            onClick={() => setNuevoContacto(p => ({ ...p, contacto_caso_sospechoso: v }))}>
                            {v}
                          </Button>
                        ))}
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Observación</Form.Label>
                      <Form.Control as="textarea" rows={2} value={nuevoContacto.observacion}
                        onChange={e => setNuevoContacto(p => ({ ...p, observacion: e.target.value }))} />
                    </Form.Group>

                    <div className="d-grid mb-3">
                      <Button variant="success" type="button" onClick={agregarContacto}>+ Agregar contacto</Button>
                    </div>

                    {errors.contactos && (
                      <div className="alert alert-danger py-2 px-3 mb-3" style={{ fontSize: 12.5 }}>
                        ⚠️ {errors.contactos}
                      </div>
                    )}

                    {form.contactos.length > 0 && (
                      <div className="d-flex flex-column gap-2">
                        {form.contactos.map((c, idx) => (
                          <div key={idx} className="d-flex justify-content-between align-items-start p-2 px-3 rounded" style={{ background: "#f5f6f0", fontSize: 12.5 }}>
                            <div>
                              <strong>{c.nombre_apellidos}</strong> — {c.tipo_identificacion} {c.documento_identidad}
                              {c.persona_enferma === "SI" && (
                                <span className="badge bg-warning text-dark ms-2" style={{ fontSize: 10 }}>enferma</span>
                              )}
                              {c.contacto_caso_sospechoso === "SI" && (
                                <span className="badge bg-danger ms-2" style={{ fontSize: 10 }}>caso sospechoso/probable</span>
                              )}
                            </div>
                            <button type="button" onClick={() => quitarContacto(idx)} className="btn btn-link text-danger p-0" style={{ fontSize: 14 }}>✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="alert alert-info py-2 px-3" style={{ fontSize: 13 }}>
                    ℹ️ No aplica — solo se registran contactos cuando la visita fue Efectiva y se encontró EISP.
                  </div>
                )
              )}

              {/* ═══ PASO 9 — Observaciones ═════════════════════════════ */}
              {step === 9 && (
                <>
                  {esEfectiva && (
                    <>
                      <h5 className="mb-3">9. Comunicación</h5>
                      <Form.Label>{req("¿Cómo se entera de la información y las actividades de la Secretaría de Salud?")}</Form.Label>
                      <div className="text-muted mb-2" style={{ fontSize: 11.5 }}>Puede marcar más de una.</div>
                      <div className="d-flex flex-wrap gap-2 mb-1">
                        {MEDIOS_INFORMACION.map((m, i) => {
                          const sel = (form.medios_informacion || []).includes(m);
                          return (
                            <Button key={m} size="sm" variant={sel ? "success" : "outline-secondary"}
                              onClick={() => set("medios_informacion", sel ? form.medios_informacion.filter(x => x !== m) : [...(form.medios_informacion || []), m])}>
                              {String.fromCharCode(97 + i)}) {m}
                            </Button>
                          );
                        })}
                      </div>
                      {errors.medios_informacion && <div className="text-danger mb-2" style={{ fontSize: "0.8em" }}>{errors.medios_informacion}</div>}

                      <div className="row g-3 mt-1 mb-4">
                        {[
                          ["sabe_vacunacion", "¿Sabe algo sobre vacunación?"],
                          ["conoce_personajes_vacunacion", "¿Sabe cuáles son los personajes (muñequitos) de la vacunación?"],
                        ].map(([campo, pregunta]) => (
                          <div className="col-12 col-md-6" key={campo}>
                            <Form.Label>{req(pregunta)}</Form.Label>
                            <div className="d-flex gap-2">
                              {["Si", "No"].map(v => (
                                <Button key={v} size="sm" style={{ minWidth: 70 }} variant={form[campo] === v ? "success" : "outline-secondary"}
                                  onClick={() => set(campo, v)}>{v === "Si" ? "SÍ" : "NO"}</Button>
                              ))}
                            </div>
                            {errors[campo] && <div className="text-danger mt-1" style={{ fontSize: "0.8em" }}>{errors[campo]}</div>}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  <h5 className="mb-3">{esEfectiva ? "Observaciones generales" : "9. Observaciones generales"}</h5>
                  <Form.Control as="textarea" rows={3} value={form.observaciones} onChange={e => set("observaciones", e.target.value)} />
                </>
              )}

              {/* ═══ PASO 10 — PAI, Vacunación Sarampión/Rubéola ═══════════ */}
              {step === 10 && (
                esEfectiva ? (
                  <>
                    <h5 className="mb-2">10. PAI — Vacunación Sarampión/Rubéola</h5>
                    <p className="text-muted mb-3" style={{ fontSize: 12.5 }}>
                      Una ficha por cada persona de la vivienda — el sistema calcula la matriz solo, no hace falta escribir ningún número a mano.
                    </p>

                    <div className="p-2 px-3 rounded mb-3 d-flex justify-content-between align-items-center" style={{ background: "#eef1e7" }}>
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: "#16342a" }}>
                        👪 Personas en la vivienda (ya preguntado antes)
                      </span>
                      <span className="badge" style={{ background: "#16342a", fontSize: 13 }}>
                        {form.pai_vacunacion.carnets.length} / {form.numero_personas_vivienda || 0}
                      </span>
                    </div>

                    <div className="p-3 rounded mb-3" style={{ background: "#f9fafb", border: "1px solid #dee2e6" }}>
                      <div className="row g-2 mb-2">
                        <div className="col-12 col-md-8">
                          <Form.Label style={{ fontSize: 10.5 }}>Nombre</Form.Label>
                          <Form.Control placeholder="Nombre completo" size="sm" value={nuevoCarnet.nombre}
                            onChange={e => setNuevoCarnet(p => ({ ...p, nombre: e.target.value }))} />
                        </div>
                        <div className="col-12 col-md-4 d-flex align-items-end">
                          <Button type="button" size="sm" className="w-100"
                            variant={nuevoCarnet.esRenuente ? "danger" : "outline-danger"}
                            onClick={() => setNuevoCarnet(p => ({ ...p, esRenuente: !p.esRenuente }))}>
                            🚫 {nuevoCarnet.esRenuente ? "Marcado como renuente" : "Renuente (no da sus datos)"}
                          </Button>
                        </div>
                      </div>

                      {nuevoCarnet.esRenuente && (
                        <div className="alert alert-warning py-2 px-3 mb-2" style={{ fontSize: 11.5 }}>
                          ⚠️ Con el nombre alcanza — no hace falta documento, edad ni ningún otro dato de esta persona.
                        </div>
                      )}

                      {!nuevoCarnet.esRenuente && (
                        <>
                          <div className="row g-2 mb-2">
                            <div className="col-6 col-md-4">
                              <Form.Label style={{ fontSize: 10.5 }}>Tipo documento</Form.Label>
                              <Form.Select size="sm" value={nuevoCarnet.tipo_identificacion}
                                onChange={e => setNuevoCarnet(p => ({ ...p, tipo_identificacion: e.target.value }))}>
                                <option value="">Tipo doc...</option>
                                {opcionesDe("tipos-documento").map(v => <option key={v} value={v}>{v}</option>)}
                              </Form.Select>
                            </div>
                            <div className="col-6 col-md-4">
                              <Form.Label style={{ fontSize: 10.5 }}>Documento</Form.Label>
                              <Form.Control placeholder="N° documento" size="sm" value={nuevoCarnet.numero_identificacion}
                                onChange={e => setNuevoCarnet(p => ({ ...p, numero_identificacion: e.target.value }))} />
                            </div>
                            <div className="col-6 col-md-2">
                              <Form.Label style={{ fontSize: 10.5 }}>Edad</Form.Label>
                              <Form.Control type="number" min={0} size="sm" value={nuevoCarnet.edad}
                                onChange={e => setNuevoCarnet(p => ({ ...p, edad: e.target.value }))} />
                            </div>
                            <div className="col-6 col-md-2">
                              <Form.Label style={{ fontSize: 10.5 }}>&nbsp;</Form.Label>
                              <Form.Select size="sm" value={nuevoCarnet.unidad_edad}
                                onChange={e => setNuevoCarnet(p => ({ ...p, unidad_edad: e.target.value }))}>
                                <option value="Años">Años</option>
                                <option value="Meses">Meses</option>
                              </Form.Select>
                            </div>
                          </div>

                      <div className="mb-2">
                        <Form.Label style={{ fontSize: 11 }}>¿Presenta carnet o hay revisión por PAIWEB?</Form.Label>
                        <div className="d-flex gap-2">
                          {["Si", "No"].map(v => (
                            <Button key={v} type="button" size="sm" className="flex-grow-1"
                              variant={nuevoCarnet.presenta_evidencia === v ? (v === "Si" ? "success" : "danger") : "outline-secondary"}
                              onClick={() => setNuevoCarnet(p => ({ ...p, presenta_evidencia: v }))}>
                              {v}
                            </Button>
                          ))}
                        </div>
                        {nuevoCarnet.presenta_evidencia === "No" && (
                          <div className="text-muted mt-1" style={{ fontSize: 10.5 }}>
                            ℹ️ Sin carnet ni validación PAIWEB queda como <strong>Renuente</strong> — el formulario de esta persona termina acá.
                          </div>
                        )}
                        {nuevoCarnet.presenta_evidencia === "Si" && esMenorDe5(nuevoCarnet) && (
                          <div className="text-muted mt-1" style={{ fontSize: 10.5 }}>ℹ️ Como tiene 5 años o menos, más adelante se pedirá el MRCV de esta persona.</div>
                        )}
                      </div>

                      {nuevoCarnet.presenta_evidencia === "Si" && !requiereFotoCarnet(nuevoCarnet) && (
                        <div className="alert alert-secondary py-2 px-3 mb-2" style={{ fontSize: 11.5 }}>
                          ℹ️ Con más de 6 años no hace falta foto del carnet — sigue directo con las preguntas de vacunación.
                        </div>
                      )}

                      {nuevoCarnet.presenta_evidencia === "Si" && requiereFotoCarnet(nuevoCarnet) && (
                        <>
                          <div className="text-muted mb-1" style={{ fontSize: 10.5 }}>
                            Sube las 2 caras del carnet, <strong>o</strong> la foto de validación PAIWEB — con una de las dos opciones alcanza.
                          </div>
                          <div className="row g-2 mb-2">
                            {[
                              { lado: "a", label: "Cara A del carnet", foto: nuevoCarnet.fotoA },
                              { lado: "b", label: "Cara B del carnet", foto: nuevoCarnet.fotoB },
                              { lado: "paiweb", label: "Foto validación PAIWEB", foto: nuevoCarnet.fotoPaiweb },
                            ].map(({ lado, label, foto }) => (
                              <div className="col-12 col-md-4" key={lado}>
                                <div className="d-flex align-items-center gap-2 p-2 rounded h-100" style={{ background: "#fff", border: "1px dashed #dee2e6" }}>
                                  <div className="rounded d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 44, height: 44, background: "#eef1e7", overflow: "hidden" }}>
                                    {foto ? <img src={foto.base64} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "🪪"}
                                  </div>
                                  <div className="flex-grow-1">
                                    <div style={{ fontSize: 10.5, fontWeight: 700, marginBottom: 2 }}>{label}</div>
                                    <div className="d-flex gap-1 flex-wrap">
                                      <label className="btn btn-outline-secondary btn-sm mb-0" style={{ fontSize: 10 }}>
                                        {comprimiendoCarnet ? "..." : "🖼️ Cargar desde galería / archivo"}
                                        <input type="file" accept="image/*" onChange={e => handleFotoCarnet(e, lado)} disabled={comprimiendoCarnet} className="d-none" />
                                      </label>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}

                      {nuevoCarnet.presenta_evidencia === "Si" && (
                        <>
                          <div className="mb-2">
                            <Form.Label style={{ fontSize: 11 }}>¿Ya tiene la vacuna de sarampión (según lo revisado)?</Form.Label>
                            <div className="d-flex gap-2">
                              {["Si", "No"].map(v => (
                                <Button key={v} type="button" size="sm" className="flex-grow-1"
                                  variant={nuevoCarnet.tiene_vacuna_sarampion === v ? (v === "Si" ? "success" : "warning") : "outline-secondary"}
                                  onClick={() => setNuevoCarnet(p => ({ ...p, tiene_vacuna_sarampion: v, se_aplico_vacuna: "", motivo_no_vacunada: "", nombre_vacunador: "", nombre_biologico: "", lote: "" }))}>
                                  {v}
                                </Button>
                              ))}
                            </div>
                          </div>

                          {nuevoCarnet.tiene_vacuna_sarampion === "No" && (
                            <div className="mb-2">
                              <Form.Label style={{ fontSize: 11 }}>¿Se le aplicó la vacuna ahora, en este barrido?</Form.Label>
                              <div className="d-flex gap-2">
                                {["Si", "No"].map(v => (
                                  <Button key={v} type="button" size="sm" className="flex-grow-1"
                                    variant={nuevoCarnet.se_aplico_vacuna === v ? (v === "Si" ? "success" : "danger") : "outline-secondary"}
                                    onClick={() => setNuevoCarnet(p => ({ ...p, se_aplico_vacuna: v }))}>
                                    {v}
                                  </Button>
                                ))}
                              </div>
                            </div>
                          )}

                          {nuevoCarnet.se_aplico_vacuna === "Si" && (
                            <div className="p-2 rounded mb-2" style={{ background: "#e4f2e8", border: "1px solid rgba(45,106,79,.3)" }}>
                              <div className="row g-2">
                                <div className="col-12 col-md-4">
                                  <Form.Label style={{ fontSize: 10.5 }}>Nombre del vacunador</Form.Label>
                                  <Form.Control size="sm" value={nuevoCarnet.nombre_vacunador} onChange={e => setNuevoCarnet(p => ({ ...p, nombre_vacunador: e.target.value }))} />
                                </div>
                                <div className="col-6 col-md-4">
                                  <Form.Label style={{ fontSize: 10.5 }}>Biológico</Form.Label>
                                  <Form.Control size="sm" value={nuevoCarnet.nombre_biologico} onChange={e => setNuevoCarnet(p => ({ ...p, nombre_biologico: e.target.value }))} />
                                </div>
                                <div className="col-6 col-md-4">
                                  <Form.Label style={{ fontSize: 10.5 }}>Lote</Form.Label>
                                  <Form.Control size="sm" value={nuevoCarnet.lote} onChange={e => setNuevoCarnet(p => ({ ...p, lote: e.target.value }))} />
                                </div>
                              </div>
                            </div>
                          )}

                          {nuevoCarnet.se_aplico_vacuna === "No" && (
                            <div className="mb-2">
                              <Form.Label style={{ fontSize: 10.5 }}>Motivo por el que no se vacunó</Form.Label>
                              <Form.Select size="sm" value={nuevoCarnet.motivo_no_vacunada} onChange={e => setNuevoCarnet(p => ({ ...p, motivo_no_vacunada: e.target.value }))}>
                                <option value="">Selecciona...</option>
                                {MOTIVOS_SIMPLE.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                              </Form.Select>
                            </div>
                          )}
                        </>
                      )}
                        </>
                      )}

                      <div className="d-flex gap-2 mt-2">
                        <Button variant={editandoCarnetIdx !== null ? "primary" : "success"} type="button" size="sm" className="flex-grow-1" onClick={agregarCarnet}>
                          {editandoCarnetIdx !== null ? "💾 Guardar cambios" : "+ Agregar persona"}
                        </Button>
                        {editandoCarnetIdx !== null && (
                          <Button variant="outline-secondary" type="button" size="sm" onClick={cancelarEdicionCarnet}>Cancelar</Button>
                        )}
                      </div>
                    </div>

                    {errors.carnets && (
                      <div className="alert alert-danger py-2 px-3 mb-3" style={{ fontSize: 12.5 }}>⚠️ {errors.carnets}</div>
                    )}

                    {form.pai_vacunacion.carnets.length > 0 && (
                      <div className="d-flex flex-column gap-2 mb-3">
                        {form.pai_vacunacion.carnets.map((c, idx) => {
                          const eraRenuente = c.presenta_evidencia !== "Si" && (c.tipo_identificacion === "N/A" || String(c.numero_identificacion || "").startsWith("RENUENTE-"));
                          const estado = eraRenuente ? { texto: "Renuente", color: "danger" }
                            : c.presenta_evidencia !== "Si" ? { texto: "No presenta carnet", color: "danger" }
                            : !c.tiene_vacuna_sarampion ? { texto: "⚠️ Incompleta", color: "dark" }
                            : c.tiene_vacuna_sarampion === "Si" ? { texto: "Vacunada previa", color: "primary" }
                            : c.se_aplico_vacuna === "Si" ? { texto: "Vacunada en barrido", color: "success" }
                            : c.se_aplico_vacuna === "No" ? { texto: "No vacunada", color: "warning" }
                            : { texto: "⚠️ Incompleta", color: "dark" };
                          return (
                            <div key={idx} className="d-flex justify-content-between align-items-center p-2 px-3 rounded" style={{ background: "#f5f6f0", fontSize: 12.5 }}>
                              <div className="d-flex align-items-center gap-2">
                                <span className={`badge bg-${estado.color}`}>{estado.texto}</span>
                                <span><strong>{c.nombre}</strong> — {c.edad} {c.unidad_edad} — {c.tipo_identificacion} {c.numero_identificacion}</span>
                              </div>
                              <div className="d-flex gap-2">
                                <button type="button" onClick={() => editarPersonaCarnet(idx)} className="btn btn-link p-0" style={{ fontSize: 14, color: "#0C447C" }}>✏️</button>
                                <button type="button" onClick={() => quitarCarnet(idx)} className="btn btn-link text-danger p-0" style={{ fontSize: 14 }}>✕</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {form.pai_vacunacion.carnets.length > 0 && (
                      <div className="p-2 px-3 rounded mb-4" style={{ background: "#eef1e7", fontSize: 11.5 }}>
                        <strong>Resumen (lo calcula el sistema):</strong>{" "}
                        {resumenPai.encontrada} encontradas · {resumenPai.vacunadaPrevia} ya vacunadas · {resumenPai.vacunadaBarrido} vacunadas hoy · {resumenPai.noVacunada} pendientes · {resumenPai.renuente} renuentes
                      </div>
                    )}

                    {(resumenPai.encontrada - resumenPai.renuente) > 0 && (
                      <>
                        <Form.Label className="fw-bold" style={{ fontSize: 12.5 }}>{req("¿Biológico pendiente?")}</Form.Label>
                        <div className="d-flex gap-2 mb-2">
                          {["No", "Si"].map(v => (
                            <Button key={v} type="button" className="flex-grow-1"
                              variant={form.pai_vacunacion.biologico_pendiente === v ? (v === "Si" ? "danger" : "success") : "outline-secondary"}
                              onClick={() => setPaiDirecto("biologico_pendiente", v)}>
                              {v}
                            </Button>
                          ))}
                        </div>
                        {form.pai_vacunacion.biologico_pendiente === "Si" && (
                          <Form.Group>
                            <Form.Label style={{ fontSize: 11.5 }}>{req("Datos del biológico pendiente")}</Form.Label>
                        <Form.Control placeholder="Ej: SRP 2ª dosis" isInvalid={!!errors.biologico_pendiente_detalle}
                          value={form.pai_vacunacion.biologico_pendiente_detalle}
                          onChange={e => setPaiDirecto("biologico_pendiente_detalle", e.target.value)} />
                        <Form.Control.Feedback type="invalid">{errors.biologico_pendiente_detalle}</Form.Control.Feedback>
                      </Form.Group>
                    )}
                      </>
                    )}
                  </>
                ) : (
                  <div className="alert alert-info py-2 px-3" style={{ fontSize: 13 }}>
                    ℹ️ No aplica — este módulo solo se registra cuando la visita fue Efectiva.
                  </div>
                )
              )}

              {/* ═══ PASO 11 — MRCV y Fotografía (Efectivas) ════════════ */}
              {step === 11 && (
                <>
                  <h5 className="mb-2">11. MRCV — Monitoreo Rápido de Cobertura de Vacunación</h5>
                  <p className="text-muted mb-3" style={{ fontSize: 12.5 }}>
                    Un MRCV por cada persona de 5 años o menos que presentó carnet — {form.mrcv.length}/{pendientesMrcv.length} completados.
                  </p>

                  {menoresSinCarnetParaMrcv.length > 0 && (
                    <div className="mb-3">
                      <p className="text-muted mb-2" style={{ fontSize: 12 }}>
                        5 años o menos, sin carnet ni validación PAIWEB — igual se les hace MRCV, con el motivo por el que no lo presentan:
                      </p>
                      <div className="d-flex flex-column gap-2">
                        {menoresSinCarnetParaMrcv.map((c, idx) => {
                          const yaCompletado = form.mrcv.some(m => m.documento_nino === c.numero_identificacion && m.tiene_carnet === "NO");
                          return (
                            <div key={idx} className="d-flex justify-content-between align-items-center p-2 px-3 rounded"
                              style={{ background: yaCompletado ? "#e4f2e8" : "#fdf3e0", fontSize: 12.5 }}>
                              <span>{yaCompletado ? "✅" : "⚠️"} <strong>{c.nombre}</strong></span>
                              {yaCompletado ? (
                                <span className="text-muted" style={{ fontSize: 11 }}>MRCV registrado</span>
                              ) : (
                                <Button size="sm" variant="warning" onClick={() => setMenorMrcvSinCarnet(c)}>
                                  Diligenciar MRCV
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {form.mrcv.length > 0 && (
                    <div className="d-flex flex-column gap-2 mb-3">
                      {form.mrcv.map((m, idx) => (
                        <div key={idx} className="d-flex justify-content-between align-items-center p-2 px-3 rounded" style={{ background: "#eef1e7", fontSize: 12.5 }}>
                          <span>✓ <strong>{m.nombre_nino}</strong></span>
                          <button type="button" onClick={() => quitarMrcv(idx)} className="btn btn-link text-danger p-0" style={{ fontSize: 14 }}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}

                  {pendientesMrcv.length > 0 && (
                    <div className="d-flex flex-column gap-2">
                      {pendientesMrcv.map((c, idx) => {
                        const yaCompletado = form.mrcv.some(m => m.documento_nino === c.numero_identificacion && m.tiene_carnet === "SI");
                        return (
                          <div key={idx} className="d-flex justify-content-between align-items-center p-2 px-3 rounded"
                            style={{ background: yaCompletado ? "#e4f2e8" : "#fff", border: yaCompletado ? "none" : "1px solid #dee2e6", fontSize: 12.5 }}>
                            <span>{yaCompletado ? "✅" : "📋"} <strong>{c.nombre}</strong> — {c.edad} {c.unidad_edad}</span>
                            {yaCompletado ? (
                              <span className="text-muted" style={{ fontSize: 11 }}>MRCV registrado</span>
                            ) : (
                              <Button size="sm" variant="success" onClick={() => setMenorMrcvConCarnet(c)}>
                                Diligenciar MRCV
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {errors.mrcv && <div className="text-danger mt-2" style={{ fontSize: "0.8em" }}>{errors.mrcv}</div>}
                </>
              )}

              {/* ═══ PASO 12 — Fotografía (Efectivas — separado de MRCV,
                    es el último paso para ellas) ═══════════════════════ */}
              {step === 12 && (
                <>
                  <h5 className="mb-3">12. Fotografía de la vivienda <span style={{ color: "#b3261e" }}>*</span></h5>
                  <div className="d-flex align-items-center gap-3 p-3 rounded" style={{ background: "#f9fafb", border: `1px dashed ${errors.foto ? "#b3261e" : "#dee2e6"}` }}>
                    <div className="rounded d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 64, height: 64, background: "#eef1e7", overflow: "hidden" }}>
                      {fotoLocal ? <img src={fotoLocal.base64} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "🏠"}
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex gap-2 flex-wrap">
                        <label className="btn btn-outline-secondary btn-sm mb-0">
                          {comprimiendoFoto ? "Procesando…" : "📷 Tomar foto"}
                          <input type="file" accept="image/*" capture="environment" onChange={handleFoto} disabled={comprimiendoFoto} className="d-none" />
                        </label>
                        <label className="btn btn-outline-secondary btn-sm mb-0">
                          {comprimiendoFoto ? "Procesando…" : "🖼️ Elegir de galería"}
                          <input type="file" accept="image/*" onChange={handleFoto} disabled={comprimiendoFoto} className="d-none" />
                        </label>
                      </div>
                      <div className="text-muted mt-1" style={{ fontSize: 11.5 }}>
                        {fotoLocal
                          ? `💾 Guardada en el dispositivo (${fotoLocal.pesoOriginalKB}KB → ${fotoLocal.pesoFinalKB}KB) — se sincroniza sola, aparte de la visita`
                          : "Ningún archivo seleccionado"}
                      </div>
                    </div>
                  </div>
                  {errors.foto && <div className="text-danger mt-2" style={{ fontSize: "0.8em" }}>{errors.foto}</div>}
                </>
              )}
            </Form>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {step > 1 && <Button variant="secondary" onClick={irAnterior} disabled={guardando}>Anterior</Button>}
        {step < TOTAL_PASOS ? (
          <Button onClick={irSiguiente} disabled={cargandoCatalogos} style={{ background: "#16342a", border: "none" }}>Siguiente</Button>
        ) : (
          <Button onClick={handleGuardar} disabled={guardando || cargandoCatalogos} style={{
            background: online ? "#16342a" : "#854F0B", border: "none",
          }}>
            {guardando ? "Guardando..." : !online ? "💾 Guardar offline" : "💾 Guardar visita"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>

    <MrcvSinCarnetModal
      show={!!menorMrcvSinCarnet}
      onClose={() => setMenorMrcvSinCarnet(null)}
      menor={menorMrcvSinCarnet}
      catalogos={catalogos}
      onGuardar={guardarMrcvSinCarnet}
    />
    <MrcvConCarnetModal
      show={!!menorMrcvConCarnet}
      onClose={() => setMenorMrcvConCarnet(null)}
      menor={menorMrcvConCarnet}
      catalogos={catalogos}
      onGuardar={guardarMrcvConCarnet}
    />
    </>
  );
}